import Cookies from "js-cookie";
import moment from "moment";
import swal from "sweetalert";
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Accordion, Select, MultiSelect, Checkbox } from "@mantine/core";
import {
  LuFileText,
  LuFileEdit,
  LuClipboardCheck,
  LuPill,
  LuMic,
  LuSparkles,
  LuX,
  LuChevronLeft,
  LuChevronDown,
  LuCheck,
  LuMessageSquare,
  LuBookOpen,
  LuBell,
  LuInfo,
  LuShieldCheck,
  LuStethoscope,
  LuPower,
  LuAlertTriangle,
  LuPlus,
} from "react-icons/lu";
import { getSinglePatients, getAllPatientsModal } from "../../services/patient";
import { getActivites } from "../../services/activity";
import { getProfile } from "../../services/profile";
import {
  decrypt,
  showInfo,
  showError,
  getOptions,
  formatDate,
} from "../../utils";
import {
  https,
  Essential_Support_Note,
  Intervention_Progress_Note,
  PROGRESS_Note,
  Billing_Services_Note,
  Group_Note,
} from "../../http";
import { getTaskTypes, getClientTask, addTask } from "../../services/task";
import { getAllFacilitiesModal } from "../../services/facility";
import { getAllPlans } from "../../services/supportplan";
import { getAllStaff } from "../../services/staff";
import {
  getAllNoteTypes,
  createNote,
  generateAiNote,
  saveNoteDraft,
  getNoteDraft,
} from "../../services/note";
import { getSkaiTokenUsage } from "../../services/skaiTokens";
import { useSpeechToText } from "../../components/NoteModal/useSpeechToText";
import { getAllSignatures } from "../../services/signature";
import CustomModal from "../../components/CustomModal/CustomModal";
import pendingModalIcon from "../../images/pendingModalIcon.png";
import MobileMarTab from "./MobileMarTab";
import { noteTypesForUser } from "../../utils/noteTypeAccess";
import { showSkaiTokenError } from "../../utils/skaiErrors";
import "./index.css";

const getInitials = (name = "") =>
  name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

// Mirrors desktop NewNoteDesignCopy severityColorMap
const severityColorMap = {
  Minor: {
    border: "rgb(166, 244, 197)",
    background: "rgb(236, 253, 243)",
    text: "rgb(2, 122, 72)",
    dot: "rgb(18, 183, 106)",
  },
  Moderate: {
    border: "rgb(254, 223, 137)",
    background: "rgb(255, 250, 235)",
    text: "rgb(181, 71, 8)",
    dot: "rgb(247, 144, 9)",
  },
  Serious: {
    border: "rgb(254, 205, 202)",
    background: "rgb(254, 243, 242)",
    text: "rgb(180, 35, 24)",
    dot: "rgb(240, 68, 56)",
  },
  Critical: {
    border: "rgb(253, 162, 155)",
    background: "rgb(254, 228, 226)",
    text: "rgb(145, 32, 24)",
    dot: "rgb(217, 45, 32)",
  },
};

const initialIncidentNoteData = {
  type: null,
  otherType: "",
  severity: null,
  clientsInvolved: [],
  staffInvolved: [],
  injurySustained: false,
  emergencyServicesCalled: false,
  restraintUsed: false,
  notifiedContacts: {},
  notificationNotes: "",
  followUpAction: "",
};

const badgeType = (module) => {
  if (module === "note") return "note";
  if (module === "mar") return "mar";
  if (module === "document") return "document";
  return "task";
};

const badgeLabel = (item) => {
  if (item?.module === "note") return "Note";
  if (item?.module === "mar") return "MAR";
  if (item?.module === "document") return "Document";
  return "Task";
};

const eventDesc = (item) => {
  if (item?.module === "note") {
    return (
      item?.moduleId?.fields?.Description ||
      item?.moduleId?.fields?.description ||
      ""
    );
  }
  if (item?.module === "mar") {
    const m = item?.moduleId;
    const parts = [
      m?.form3?.medicationName,
      m?.form3?.dosage && m?.form3?.unit
        ? `${m.form3.dosage} ${m.form3.unit}`
        : null,
      "· marked taken",
    ].filter(Boolean);
    return parts.join(" ");
  }
  if (item?.module === "document") {
    return "Document uploaded";
  }
  return "";
};

// ── Icons ──────────────────────────────────────────────────────────────────

const FacesheetIcon = () => <LuFileText size={18} />;
const NoteIcon = () => <LuFileEdit size={18} />;
const TaskIcon = () => <LuClipboardCheck size={18} />;
const MarIcon = () => <LuPill size={18} />;
const MicIcon = () => <LuMic size={18} />;
const AiStarIcon = () => <LuSparkles size={14} />;
const CloseXIcon = () => <LuX size={16} strokeWidth={2.5} />;

// ── Shared client bar (used in Task + Note forms) ──────────────────────────
const ClientBar = ({ clientName, clientDetails }) => {
  const initials = (clientName || "")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("");
  const shortId = clientDetails?.id?.slice(-6) || "";
  return (
    <div className="mc-client-bar">
      <div className="mc-client-bar-photo">
        <div className="mc-client-bar-photo-inner">{initials}</div>
      </div>
      <div className="mc-client-bar-info">
        <div className="mc-client-bar-name">{clientName}</div>
        {shortId && (
          <div className="mc-client-bar-meta">
            <span>{shortId}</span>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Note initial shapes ────────────────────────────────────────────────────
const mkInitialDesc = () => ({
  id: Date.now(),
  outcomeTitle: "",
  outcomeNumber: "",
  outComeCategory: "",
  description: "",
  details: null,
  error: "",
  planId: "1",
});

const mkInitialES = () => ({
  id: Date.now(),
  essentialSupportType: "",
  howOftenEssentialSupport: "",
  essentialNumber: "",
  essentialActivityNumber: "",
  description: "",
});

const mkInitialGoal = () => ({
  id: Date.now(),
  identifiedGoal: "",
  goalNumber: "",
  goalNote: "",
  startTimeGoal: "",
  endTimeGoal: "",
  goalActivities: [
    { id: Date.now(), objectives: "", Intervention_Instruction: "" },
  ],
  behaviorDescription: "",
  responseDescription: "",
  planDescription: "",
  metGoalIntervention: "",
});

const TABS = [
  { key: "facesheet", label: "Facesheet", Icon: FacesheetIcon },
  { key: "note", label: "Note", Icon: NoteIcon },
  { key: "task", label: "Task", Icon: TaskIcon },
  { key: "mar", label: "MAR", Icon: MarIcon },
];

// ── Main component ─────────────────────────────────────────────────────────

const MobileChart = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const token = Cookies.get("token");
  const { profile } = useSelector((state) => state?.profile);

  const qrSession = (() => {
    try {
      return JSON.parse(localStorage.getItem("qr_session"));
    } catch {
      return null;
    }
  })();

  const clientId = location.state?.clientId || qrSession?.clientId;

  const [activeTab, setActiveTab] = useState("facesheet");

  const handleTabSwitch = (tab) => {
    setActiveTab(tab);
    if (tab === "facesheet") fetchActivities();
  };
  const [clientDetails, setClientDetails] = useState(null);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activityLoading, setActivityLoading] = useState(false);

  useEffect(() => {
    if (!token) {
      navigate("/login");
      return;
    }
    if (!clientId) {
      navigate("/login");
      return;
    }
    if (!profile) getProfile(false, token);
    fetchClient();
    fetchActivities();
  }, [clientId]);

  const fetchClient = async () => {
    const res = await getSinglePatients(clientId);
    setClientDetails(res);
    setLoading(false);
  };

  const fetchActivities = async () => {
    setActivityLoading(true);
    const data = await getActivites(clientId);
    if (data) setActivities(data);
    setActivityLoading(false);
  };

  const handleLogout = () => {
    const session = (() => {
      try {
        return JSON.parse(localStorage.getItem("qr_session"));
      } catch {
        return null;
      }
    })();
    const cId = session?.clientId || clientId;
    const cName = session?.name || "";
    const cRef = session?.ref || "";
    localStorage.removeItem("qr_session");
    Cookies.remove("token");
    if (cId) {
      navigate(
        `/scan/${cId}?name=${encodeURIComponent(cName)}&ref=${encodeURIComponent(cRef)}`,
      );
    } else {
      navigate("/login");
    }
  };

  const handleScanAgain = () => {
    const session = (() => {
      try {
        return JSON.parse(localStorage.getItem("qr_session"));
      } catch {
        return null;
      }
    })();
    const cId = session?.clientId || clientId;
    const cName = session?.name || "";
    const cRef = session?.ref || "";
    localStorage.removeItem("qr_session");
    Cookies.remove("token");
    if (cId) {
      navigate(
        `/scan/${cId}?name=${encodeURIComponent(cName)}&ref=${encodeURIComponent(cRef)}`,
      );
    } else {
      navigate("/login");
    }
  };

  const shortId = clientDetails?.id?.slice(-6) || qrSession?.ref || "";
  const clientName = clientDetails?.name || qrSession?.name || "Client";
  const staffName =
    profile?.user?.name ||
    `${profile?.user?.firstName || ""} ${profile?.user?.lastName || ""}`.trim() ||
    "Staff";
  const staffRole = profile?.user?.role || profile?.user?.type || "";
  const staffInitials = getInitials(staffName);

  if (!token) return null;

  if (loading) {
    return (
      <div className="mc-root">
        <div className="mc-screen">
          <div className="mc-full-center">
            <div className="mc-spinner" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mc-root">
      <div className="mc-screen">
        {/* ── App bar — only on facesheet ─────────────────────────────── */}
        {activeTab === "facesheet" && (
          <header className="mc-app-bar">
            <span></span>
            <div className="mc-brand">
              <div className="mc-brand-mark">O</div>
              <span className="mc-brand-name">Ogiso</span>
            </div>
            <div className="mc-staff-avatar" aria-label="Your account">
              {staffInitials}
            </div>
          </header>
        )}

        {/* ── Tab content ─────────────────────────────────────────────── */}
        <div className="mc-content">
          {activeTab === "facesheet" && (
            <FacesheetTab
              clientDetails={clientDetails}
              clientName={clientName}
              shortId={shortId}
              activities={activities}
              activityLoading={activityLoading}
              staffName={staffName}
              staffInitials={staffInitials}
              staffRole={staffRole}
              onTabSwitch={handleTabSwitch}
              onLogout={handleLogout}
              onScanAgain={handleScanAgain}
            />
          )}
          {activeTab === "note" && (
            <NoteTab
              clientId={clientId}
              clientName={clientName}
              clientDetails={clientDetails}
              onTabSwitch={handleTabSwitch}
            />
          )}
          {activeTab === "task" && (
            <TaskTab
              clientId={clientId}
              clientName={clientName}
              clientDetails={clientDetails}
              onTabSwitch={handleTabSwitch}
            />
          )}
          {activeTab === "mar" && (
            <MobileMarTab
              clientId={clientId}
              clientDetails={clientDetails}
              onTabSwitch={handleTabSwitch}
            />
          )}
        </div>

        {/* ── Bottom tab bar ──────────────────────────────────────────── */}
        {/* <nav className="mc-tab-bar" aria-label="Charting tabs">
          {TABS.map(({ key, label, Icon }) => (
            <button
              key={key}
              className={`mc-tab-btn${activeTab === key ? " mc-tab-btn--active" : ""}`}
              type="button"
              onClick={() => handleTabSwitch(key)}
              aria-current={activeTab === key ? "page" : undefined}
            >
              <span className="mc-tab-label">{label}</span>
            </button>
          ))}
        </nav> */}
      </div>
    </div>
  );
};

// ── Facesheet (Hub) tab ────────────────────────────────────────────────────

const FacesheetTab = ({
  clientDetails,
  clientName,
  shortId,
  activities,
  activityLoading,
  staffName,
  staffInitials,
  staffRole,
  onTabSwitch,
  onLogout,
  onScanAgain,
}) => {
  const form1 = clientDetails?.form1;
  const imgSrc = clientDetails?.imageurl
    ? decrypt(clientDetails.imageurl)
    : null;

  return (
    <div className="mc-facesheet">
      {/* ── Client card ───────────────────────────────────────────────── */}
      <section className="mc-client-card">
        <div className="mc-client-head">
          <div className="mc-photo-ring">
            {imgSrc ? (
              <img src={imgSrc} alt={clientName} className="mc-photo-img" />
            ) : (
              <div className="mc-photo-placeholder">
                {getInitials(clientName)}
              </div>
            )}
          </div>
          <div className="mc-client-info">
            <h1 className="mc-client-name">{clientName}</h1>
            <div className="mc-client-meta">
              <code className="mc-client-code">{shortId}</code>
              {form1?.gender && (
                <span>
                  {form1.gender}
                  {form1?.dob &&
                    ` · ${moment().diff(moment(form1.dob), "years")} yrs · DOB ${moment(form1.dob).format("MM/DD/YYYY")}`}
                </span>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ── Quick actions ─────────────────────────────────────────────── */}
      <div className="mc-section-label">Quick actions</div>
      <div className="mc-quick-grid">
        <button
          className="mc-tile mc-tile--accent"
          type="button"
          onClick={() => onTabSwitch("note")}
        >
          <div className="mc-tile-icon">
            <NoteIcon />
          </div>
          <div>
            <div className="mc-tile-title">New note</div>
            <div className="mc-tile-sub">AI · voice · text</div>
          </div>
        </button>

        <button
          className="mc-tile"
          type="button"
          onClick={() => onTabSwitch("task")}
        >
          <div className="mc-tile-icon">
            <TaskIcon />
          </div>
          <div>
            <div className="mc-tile-title">Add task</div>
            <div className="mc-tile-sub">
              {(() => {
                const today = moment().format("YYYY-MM-DD");
                const count = activities.filter(
                  (a) =>
                    a.module === "task" &&
                    moment(a.createdAt).format("YYYY-MM-DD") === today,
                ).length;
                return count > 0 ? `${count} open today` : "";
              })()}
            </div>
          </div>
          {(() => {
            const today = moment().format("YYYY-MM-DD");
            const count = activities.filter(
              (a) =>
                a.module === "task" &&
                moment(a.createdAt).format("YYYY-MM-DD") === today,
            ).length;
            return count > 0 ? (
              <span className="mc-tile-badge">{count}</span>
            ) : null;
          })()}
        </button>

        <button
          className="mc-tile"
          type="button"
          onClick={() => onTabSwitch("mar")}
        >
          <div className="mc-tile-icon">
            <MarIcon />
          </div>
          <div>
            <div className="mc-tile-title">MAR</div>
            <div className="mc-tile-sub">Daily medications</div>
          </div>
        </button>

        <button className="mc-tile" type="button">
          <div className="mc-tile-icon">
            <FacesheetIcon />
          </div>
          <div>
            <div className="mc-tile-title">Facesheet</div>
            <div className="mc-tile-sub">Full chart</div>
          </div>
        </button>
      </div>

      {/* ── Recent activity ───────────────────────────────────────────── */}
      <div className="mc-section-row">
        <span className="mc-section-label">Recent activity</span>
        <span className="mc-section-meta">last 3 days</span>
      </div>

      <div className="mc-activity-list">
        {activityLoading && (
          <div className="mc-activity-center">
            <div className="mc-spinner mc-spinner--sm" />
          </div>
        )}
        {!activityLoading && activities.length === 0 && (
          <div className="mc-activity-center mc-activity-empty">
            No recent activity
          </div>
        )}
        {!activityLoading &&
          activities.slice(0, 10).map((item, i) => {
            const type = badgeType(item?.module);
            const label = badgeLabel(item);
            const isTask =
              item?.module === "taskFields" || item?.module === "task";
            const isNote = item?.module === "note";

            const noteTypeName = isNote
              ? item?.moduleId?.fields?.noteType || "Note"
              : null;

            const taskTypeName = isTask
              ? item?.moduleId?.type?.name ||
                item?.moduleId?.type ||
                item?.moduleId?.taskType?.name ||
                item?.moduleId?.taskType ||
                item?.moduleId?.name ||
                "Task"
              : null;

            const subtasks = isTask
              ? item?.moduleId?.fieldsDone
                  ?.map((v) => v?.field?.name)
                  .filter(Boolean) || []
              : [];

            const desc = !isTask ? eventDesc(item) : null;

            return (
              <div key={i} className="mc-activity-item">
                <span className={`mc-badge mc-badge--${type}`}>{label}</span>
                <div className="mc-activity-body">
                  {/* Note — type name only, no description */}
                  {isNote && <p className="mc-activity-text">{noteTypeName}</p>}

                  {/* MAR / Document — description only */}
                  {!isNote && !isTask && desc && (
                    <p className="mc-activity-text">{desc}</p>
                  )}

                  {/* Task — type name + subtask chips */}
                  {isTask && <p className="mc-activity-text">{taskTypeName}</p>}
                  {subtasks.length > 0 && (
                    <div className="mc-subtasks">
                      {subtasks.map((name, si) => (
                        <span key={si} className="mc-subtask">
                          {name}
                        </span>
                      ))}
                    </div>
                  )}

                  <div className="mc-activity-stamp">
                    <span className="mc-activity-client">{clientName}</span>
                    <span className="mc-sep" />
                    <span>{moment(item?.createdAt).format("MMM D, YYYY")}</span>
                    <span className="mc-sep" />
                    <span>{moment(item?.createdAt).format("h:mm A")}</span>
                  </div>
                </div>
                <div
                  className="mc-activity-avatar"
                  title={item?.createdBy?.name}
                >
                  {getInitials(item?.createdBy?.name || "")}
                </div>
              </div>
            );
          })}
      </div>

      {/* ── Signed in as ──────────────────────────────────────────────── */}
      <div className="mc-signed-in">
        <div className="mc-signed-in-avatar">{staffInitials}</div>
        <div className="mc-signed-in-body">
          <div className="mc-signed-in-label">Signed in as</div>
          <div className="mc-signed-in-name">
            {staffName}
            {staffRole ? ` · ${staffRole}` : ""}
          </div>
        </div>
        <button className="mc-signed-in-btn" type="button" onClick={onLogout}>
          Switch
        </button>
      </div>

      {/* ── Wrong client ──────────────────────────────────────────────── */}
      {/* <p className="mc-scan-again">
        Not the right client?{" "}
        <button className="mc-scan-link" type="button" onClick={onScanAgain}>
          Scan again
        </button>
      </p> */}
    </div>
  );
};

// ── Task Tab ───────────────────────────────────────────────────────────────

const TaskTab = ({ clientId, clientName, clientDetails, onTabSwitch }) => {
  const { taskTypes } = useSelector((s) => s.task);

  useEffect(() => {
    getTaskTypes("all");
  }, [clientId]);

  return (
    <AddTaskForm
      clientId={clientId}
      clientName={clientName}
      clientDetails={clientDetails}
      taskTypes={taskTypes}
      onTabSwitch={onTabSwitch}
    />
  );
};

// ── Add Task Form ──────────────────────────────────────────────────────────

const SHIFTS = ["Morning", "Evening", "Night"];

const LEVEL_OF_NEED_OPTIONS = [
  "LON 1 - Intermittent",
  "LON 5 - Limited",
  "LON 8 - Extensive",
  "LON 6 - Pervasive",
  "LON 9 - Pervasive+",
];

const COMMUNITY_INTEGRATION_NAME = "Community Integration (for off-site only)";

const ADL_HIDDEN_FIELDS = {
  "Vital Signs": ["Pain Assessment", "Intake and Output Monitoring"],
  Bowel: ["Blood Pressure"],
  "Skin Care": [
    "Check Skin Integrity",
    "Check Boney Areas",
    "Apply cream/ointment as Prescribed",
    "Maintain good nutrition and fluid intake",
    "Perineal care with brief change",
    "Pressure relieving device",
    "Float Heals",
  ],
  Bed: [
    "Room Safety Tidy",
    "Call Light Access Check",
    "Bed Positioning",
    "Toe Nails/Finger Nails",
  ],
  Other: ["Family Communication Support", "Mood Observation"],
};

const AddTaskForm = ({
  clientId,
  clientName,
  clientDetails,
  taskTypes,
  onTabSwitch,
}) => {
  const rawFacilities = useSelector((s) => s.facility?.allFacilities);
  const allFacilities = Array.isArray(rawFacilities)
    ? rawFacilities
    : rawFacilities?.facilities || rawFacilities?.data || [];
  const allPlans = useSelector((s) => s.supportPlan?.allPlans) || [];
  const { profile } = useSelector((s) => s.profile);
  const { staff: staffStore } = useSelector((s) => s.staff);
  const staffId = profile?.user?.id || profile?.user?._id || "";
  const userType = profile?.user?.type || profile?.user?.role || "";

  const [selectedStaff, setSelectedStaff] = useState("");

  const activeStaffData =
    staffStore?.staffs
      ?.filter((s) => s.active === true && !s.deleted)
      ?.map((s) => ({ value: s.id || s._id, label: s.name })) || [];

  // ── Core fields ───────────────────────────────────────────────────────────
  const [taskType, setTaskType] = useState("");
  const [taskTypeName, setTaskTypeName] = useState("");
  const [shift, setShift] = useState("");
  const [date, setDate] = useState(moment().format("YYYY-MM-DD"));
  const [facility, setFacility] = useState("");
  const [taskTypeDetails, setTaskTypeDetails] = useState(null);
  const [checks, setChecks] = useState([]);
  const [requiredCheckedFields, setRequiredCheckedFields] = useState([]);
  const [comments, setComments] = useState([]);
  const [showComment, setShowComment] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [loadingFields, setLoadingFields] = useState(false);
  const [adlSnapshot, setAdlSnapshot] = useState(null);
  const [activePlan, setActivePlan] = useState(null);

  // ── Individualised Skills extra fields ────────────────────────────────────
  const [providerLicense, setProviderLicense] = useState("");
  const [placeOfService, setPlaceOfService] = useState("");
  const [stateVal, setStateVal] = useState("");
  const [zipcode, setZipcode] = useState("");
  const [site, setSite] = useState("");
  const [levelofneed, setLevelofneed] = useState("");
  const [timeIn, setTimeIn] = useState("");
  const [timeOut, setTimeOut] = useState("");

  const isIndividualizedSkills =
    taskTypeName === "Individualized Skills and Socialization Log";

  const showAdlForTaskType =
    taskTypeName === "Activities of Daily Living" ||
    taskTypeDetails?.includeAdlMatrix === true;

  const clientFacilityId =
    clientDetails?.facility?._id ||
    (typeof clientDetails?.facility === "string"
      ? clientDetails.facility
      : null) ||
    clientDetails?.form2?.facility?._id ||
    (typeof clientDetails?.form2?.facility === "string"
      ? clientDetails.form2.facility
      : null) ||
    "";

  useEffect(() => {
    if (!clientFacilityId) getAllFacilitiesModal("all", false);
    if (clientFacilityId) setFacility(clientFacilityId);
    getAllPlans(clientId, false);
    getAllStaff("all");
    setSelectedStaff(staffId);
  }, []);

  // ADL snapshot (for adlMatrixSnapshot in payload)
  useEffect(() => {
    if (!showAdlForTaskType || !clientId || !(facility || clientFacilityId)) {
      setAdlSnapshot(null);
      return;
    }
    const facilityId = facility || clientFacilityId;
    const plan = (allPlans || []).find(
      (v) =>
        (v?.client?._id === clientId || v?.client?.id === clientId) &&
        (v?.facility?._id === facilityId || v?.facility?.id === facilityId) &&
        v?.status === "active" &&
        v?.isActive === true &&
        v?.deleted !== true &&
        v?.servicePlanData?.adlInstructions?.enabled &&
        v?.servicePlanData?.adlInstructions?.rows?.length > 0,
    );
    setAdlSnapshot(plan ? plan.servicePlanData.adlInstructions.rows : null);
  }, [showAdlForTaskType, clientId, facility, clientFacilityId, allPlans]);

  // activePlan (for ADL subtask filtering — mirrors desktop fetchActivePlan)
  useEffect(() => {
    if (taskTypeName !== "Activities of Daily Living") {
      setActivePlan(null);
      return;
    }
    const facilityId = facility || clientFacilityId;
    if (!clientId || !facilityId) {
      setActivePlan(null);
      return;
    }
    const plan = (allPlans || []).find(
      (v) =>
        (v?.client?._id === clientId || v?.client?.id === clientId) &&
        (v?.facility?._id === facilityId || v?.facility?.id === facilityId) &&
        v?.status === "active" &&
        v?.isActive === true &&
        v?.deleted !== true &&
        v?.servicePlanData?.activitiesOfDailyLiving,
    );
    setActivePlan(plan?.servicePlanData || null);
  }, [taskTypeName, clientId, facility, clientFacilityId, allPlans]);

  // Auto-fill Individualized Skills fields from facility record (mirrors desktop)
  useEffect(() => {
    if (!isIndividualizedSkills) return;
    const facilityId = facility || clientFacilityId;
    if (!facilityId) return;
    const f = allFacilities.find((f) => (f._id || f.id) === facilityId);
    if (!f) return;
    setPlaceOfService(f?.address || "");
    setStateVal(f?.state || "");
    setZipcode(f?.zipcode || "");
    setProviderLicense(f?.providerLicense || "");
  }, [isIndividualizedSkills, facility, clientFacilityId, allFacilities]);

  // Auto-fill levelofneed from client profile (mirrors desktop)
  useEffect(() => {
    const lon = clientDetails?.form1?.levelofneed;
    if (lon) setLevelofneed(lon.replace(/–/g, "-"));
  }, [clientDetails]);

  // checkedFieldIds — set of field _id strings that are checked (for UI display)
  const [checkedFieldIds, setCheckedFieldIds] = useState(new Set());
  // fieldTexts — map of field _id -> textField value
  const [fieldTexts, setFieldTexts] = useState({});

  const loadTaskTypeFields = async (typeId, facilityId) => {
    if (!typeId || !facilityId) return;
    setLoadingFields(true);
    try {
      const data = await getClientTask({ client: clientId, type: typeId });
      const td = data?.taskType || null;
      setTaskTypeDetails(td);
      setChecks([]);
      setCheckedFieldIds(new Set());
      setFieldTexts({});
    } finally {
      setLoadingFields(false);
    }
  };

  const handleTaskTypeChange = async (typeId) => {
    setTaskType(typeId);
    setTaskTypeDetails(null);
    setChecks([]);
    setCheckedFieldIds(new Set());
    setFieldTexts({});
    setRequiredCheckedFields([]);
    const found = (taskTypes || []).find((t) => (t._id || t.id) === typeId);
    setTaskTypeName(found?.name || "");
    if (!typeId) return;
    const resolvedFacility = facility || clientFacilityId;
    if (!resolvedFacility) return;
    await loadTaskTypeFields(typeId, resolvedFacility);
  };

  const handleFacilityChange = async (facilityId) => {
    setFacility(facilityId);
    setTaskTypeDetails(null);
    setChecks([]);
    setCheckedFieldIds(new Set());
    setFieldTexts({});
    setRequiredCheckedFields([]);
    if (!facilityId || !taskType) return;
    await loadTaskTypeFields(taskType, facilityId);
  };

  // Mirror desktop AccordionCheck: add field to checks on check, remove on uncheck
  const toggleCheck = (fieldId) => {
    setCheckedFieldIds((prev) => {
      const next = new Set(prev);
      if (next.has(fieldId)) {
        next.delete(fieldId);
        setChecks((c) => c.filter((item) => item.field !== fieldId));
      } else {
        next.add(fieldId);
        setChecks((c) => [
          ...c,
          {
            field: fieldId,
            staff: selectedStaff || staffId,
            shift,
            textField: fieldTexts[fieldId] || "",
          },
        ]);
      }
      return next;
    });
  };

  const updateCheckText = (fieldId, value) => {
    setFieldTexts((prev) => ({ ...prev, [fieldId]: value }));
    setChecks((prev) =>
      prev.map((c) => (c.field === fieldId ? { ...c, textField: value } : c)),
    );
    if (value.trim()) {
      setRequiredCheckedFields((prev) => prev.filter((id) => id !== fieldId));
    }
  };

  const resetForm = () => {
    setTaskType("");
    setTaskTypeName("");
    setShift("Morning");
    setDate(moment().format("YYYY-MM-DD"));
    setFacility(clientFacilityId || "");
    setTaskTypeDetails(null);
    setChecks([]);
    setCheckedFieldIds(new Set());
    setFieldTexts({});
    setRequiredCheckedFields([]);
    setComments([]);
    setShowComment(false);
    setAdlSnapshot(null);
    setActivePlan(null);
    setProviderLicense("");
    setPlaceOfService("");
    setStateVal("");
    setZipcode("");
    setSite("");
    setLevelofneed("");
    setTimeIn("");
    setTimeOut("");
    setSelectedStaff(staffId);
  };

  // Mirror desktop AddTask.jsx subtask filtering logic exactly
  const getFilteredSubTasks = () => {
    if (!taskTypeDetails?.subTasks) return [];
    const shouldHideFields =
      taskTypeName === "Activities of Daily Living" && !activePlan;
    return taskTypeDetails.subTasks
      .filter((sub) => {
        const name = sub?.task?.name;
        if (name === COMMUNITY_INTEGRATION_NAME) return site === "Off-Site";
        if (name === "Meals") return true;
        if (name === "Body Care" && activePlan) return false;
        if (
          taskTypeName === "Activities of Daily Living" &&
          !activePlan &&
          (name === "Hygiene" || name === "Nutrition and Hydration")
        )
          return false;
        if (activePlan?.activitiesOfDailyLiving?.fieldsDone?.length > 0) {
          const planFieldIds =
            activePlan.activitiesOfDailyLiving.fieldsDone.map((fd) => fd.field);
          return sub?.fields?.some((f) => planFieldIds.includes(f._id));
        }
        return true;
      })
      .map((sub) => {
        const name = sub?.task?.name;
        let fields;
        if (name === "Meals") {
          fields = sub?.fields || [];
        } else if (
          activePlan?.activitiesOfDailyLiving?.fieldsDone?.length > 0
        ) {
          const planFieldIds =
            activePlan.activitiesOfDailyLiving.fieldsDone.map((fd) =>
              String(fd.field),
            );
          fields = (sub?.fields || []).filter((f) =>
            planFieldIds.includes(String(f._id)),
          );
          if (shouldHideFields) {
            fields = fields.filter(
              (f) => !ADL_HIDDEN_FIELDS[name]?.includes(f?.name),
            );
          }
        } else {
          fields = shouldHideFields
            ? (sub?.fields || []).filter(
                (f) => !ADL_HIDDEN_FIELDS[name]?.includes(f?.name),
              )
            : sub?.fields || [];
        }
        return { ...sub, fields };
      });
  };

  const handleSave = async () => {
    if (!taskType) return;

    // Mirror desktop onSave validation exactly — required fields checked or not
    if (taskTypeDetails?.general !== true) {
      const invalidFields = [];
      taskTypeDetails?.subTasks?.forEach((sub) => {
        sub.fields?.forEach((field) => {
          if (field.required) {
            const checkEntry = checks.find((c) => c.field === field._id);
            if (
              checkEntry &&
              (!checkEntry.textField || checkEntry.textField.trim() === "")
            ) {
              invalidFields.push(field);
            }
          }
        });
      });
      if (invalidFields.length > 0) {
        setRequiredCheckedFields(invalidFields.map((f) => f._id));
        return;
      }
    }

    // Mirror desktop comment required check
    if (taskTypeDetails?.commentRequired && comments.length === 0) {
      showError("A comment is required for this task type");
      return;
    }

    const formattedComments = comments.map((c) => (c?.text ? c.text : c));

    const payload = {
      client: clientId,
      facility: facility || clientFacilityId || undefined,
      staff: selectedStaff || staffId || undefined,
      shift,
      date: new Date(),
      taskType,
      isStarted: true,
      notes: "",
      comments: formattedComments,
      fieldsDone: checks,
      ...(showAdlForTaskType &&
        adlSnapshot?.length > 0 && {
          adlMatrixSnapshot: adlSnapshot,
        }),
      ...(isIndividualizedSkills && {
        providerLicense,
        placeOfService,
        state: stateVal,
        zipcode,
        site,
        levelofneed,
        timeIn,
        timeOut,
      }),
    };

    setFormLoading(true);
    try {
      await addTask(
        payload,
        resetForm,
        () => onTabSwitch("facesheet"),
        setFormLoading,
      );
    } catch (err) {
      showError(
        err?.response?.data?.message || "Failed to save task",
        err?.response?.status,
      );
      setFormLoading(false);
    }
  };

  return (
    <div className="mc-add-form">
      {/* ── App bar ─────────────────────────────────────────────────────── */}
      <div className="mc-form-app-bar">
        <button
          className="mc-icon-btn"
          type="button"
          onClick={() => onTabSwitch("facesheet")}
          aria-label="Back"
        >
          <LuChevronLeft size={18} strokeWidth={2.5} />
        </button>
        <div className="mc-form-app-bar-title">New task</div>
        <div className="mc-icon-btn" style={{ visibility: "hidden" }} />
      </div>

      {/* ── Client bar ──────────────────────────────────────────────────── */}
      <ClientBar clientName={clientName} clientDetails={clientDetails} />

      {/* ── Scrollable body ─────────────────────────────────────────────── */}
      <div className="mc-form-body">
        {/* Staff */}
        <div className="mc-form-field">
          <Select
            label="Staff Name"
            placeholder="Select staff…"
            data={activeStaffData}
            value={selectedStaff || null}
            disabled={userType === "staff"}
            onChange={(val) => setSelectedStaff(val || "")}
            searchable
            rightSection={<LuChevronDown size={14} color="#6B7280" />}
            rightSectionPointerEvents="none"
            classNames={{
              input: "mc-mantine-select-input",
              dropdown: "mc-mantine-dropdown",
            }}
          />
        </div>

        {/* Task type */}
        <div className="mc-form-field">
          <Select
            label="Task type"
            placeholder="Select task type…"
            data={(taskTypes || []).map((t) => ({
              value: t._id || t.id,
              label: t.name,
            }))}
            value={taskType || null}
            onChange={(val) => handleTaskTypeChange(val || "")}
            searchable
            rightSection={<LuChevronDown size={14} color="#6B7280" />}
            rightSectionPointerEvents="none"
            classNames={{
              input: "mc-mantine-select-input",
              dropdown: "mc-mantine-dropdown",
            }}
          />
        </div>

        {/* Facility — only shown if it can't be derived from the client */}
        {!clientFacilityId && (
          <div className="mc-form-field">
            <Select
              label="Facility"
              placeholder="Select facility…"
              data={(allFacilities || [])
                .filter((f) =>
                  (clientDetails?.facilityId || []).some(
                    (_f) => _f?.id == (f._id || f.id),
                  ),
                )
                .map((f) => ({ value: f._id || f.id, label: f.name }))}
              value={facility || null}
              onChange={(val) => handleFacilityChange(val || "")}
              searchable
              rightSection={<LuChevronDown size={14} color="#6B7280" />}
              rightSectionPointerEvents="none"
              classNames={{
                input: "mc-mantine-select-input",
                dropdown: "mc-mantine-dropdown",
              }}
            />
          </div>
        )}

        {/* Date */}
        <div className="mc-form-field">
          <label className="mc-form-label">Date</label>
          <input
            className="mc-form-input"
            type="date"
            value={date}
            max={moment().format("YYYY-MM-DD")}
            onChange={(e) => setDate(e.target.value)}
          />
        </div>

        {/* ── Standard task type: Shift ────────────────────────────────── */}
        {!isIndividualizedSkills && (
          <div className="mc-form-field">
            <Select
              label="Shift"
              data={SHIFTS.map((s) => ({ value: s, label: s }))}
              value={shift || null}
              placeholder="Select shift…"
              onChange={(val) => setShift(val || "")}
              searchable
              rightSection={<LuChevronDown size={14} color="#6B7280" />}
              rightSectionPointerEvents="none"
              classNames={{
                input: "mc-mantine-select-input",
                dropdown: "mc-mantine-dropdown",
              }}
            />
          </div>
        )}

        {/* ── Individualised Skills extra fields ──────────────────────── */}
        {isIndividualizedSkills && (
          <>
            <div className="mc-form-field">
              <label className="mc-form-label">License Number</label>
              <input
                className="mc-form-input"
                type="text"
                value={providerLicense}
                onChange={(e) => setProviderLicense(e.target.value)}
                placeholder="Add License Number"
              />
            </div>

            <div className="mc-form-field">
              <label className="mc-form-label">Place of Service</label>
              <input
                className="mc-form-input"
                type="text"
                value={placeOfService}
                onChange={(e) => setPlaceOfService(e.target.value)}
                placeholder="Place of Service"
              />
            </div>

            <div className="mc-form-row-2">
              <div className="mc-form-field">
                <label className="mc-form-label">State</label>
                <input
                  className="mc-form-input"
                  type="text"
                  value={stateVal}
                  onChange={(e) => setStateVal(e.target.value)}
                  placeholder="State"
                />
              </div>
              <div className="mc-form-field">
                <label className="mc-form-label">Zip Code</label>
                <input
                  className="mc-form-input"
                  type="text"
                  value={zipcode}
                  onChange={(e) => setZipcode(e.target.value)}
                  placeholder="Zip Code"
                />
              </div>
            </div>

            <div className="mc-form-field">
              <Select
                label="Site"
                placeholder="Select site…"
                data={[
                  { value: "On-Site", label: "On-Site" },
                  { value: "Off-Site", label: "Off-Site" },
                ]}
                value={site || null}
                onChange={(val) => setSite(val || "")}
                searchable
                rightSection={<LuChevronDown size={14} color="#6B7280" />}
                rightSectionPointerEvents="none"
                classNames={{
                  input: "mc-mantine-select-input",
                  dropdown: "mc-mantine-dropdown",
                }}
              />
            </div>

            <div className="mc-form-field">
              <Select
                label="Shift"
                data={SHIFTS.map((s) => ({ value: s, label: s }))}
                value={shift || null}
                onChange={(val) => setShift(val || "")}
                searchable
                rightSection={<LuChevronDown size={14} color="#6B7280" />}
                rightSectionPointerEvents="none"
                classNames={{
                  input: "mc-mantine-select-input",
                  dropdown: "mc-mantine-dropdown",
                }}
              />
            </div>

            <div className="mc-form-field">
              <Select
                label="Level of Need"
                placeholder="Select Level of Need…"
                data={LEVEL_OF_NEED_OPTIONS.map((o) => ({
                  value: o,
                  label: o,
                }))}
                value={levelofneed || null}
                onChange={(val) => setLevelofneed(val || "")}
                searchable
                rightSection={<LuChevronDown size={14} color="#6B7280" />}
                rightSectionPointerEvents="none"
                classNames={{
                  input: "mc-mantine-select-input",
                  dropdown: "mc-mantine-dropdown",
                }}
              />
            </div>

            <div className="mc-form-row-2">
              <div className="mc-form-field">
                <label className="mc-form-label">Time In</label>
                <input
                  className="mc-form-input"
                  type="time"
                  value={timeIn}
                  onChange={(e) => setTimeIn(e.target.value)}
                />
              </div>
              <div className="mc-form-field">
                <label className="mc-form-label">Time Out</label>
                <input
                  className="mc-form-input"
                  type="time"
                  value={timeOut}
                  onChange={(e) => setTimeOut(e.target.value)}
                />
              </div>
            </div>
          </>
        )}

        {/* ── Subtask field groups ─────────────────────────────────────── */}
        {loadingFields && (
          <div className="mc-task-center">
            <div className="mc-spinner mc-spinner--sm" />
          </div>
        )}

        {!loadingFields &&
          getFilteredSubTasks().some((s) => s.fields?.length > 0) && (
            <Accordion
              multiple
              defaultValue={getFilteredSubTasks()
                .map((sub, gi) => (sub.fields?.length > 0 ? String(gi) : null))
                .filter(Boolean)}
              variant="separated"
              radius="md"
              classNames={{
                root: "mc-accordion-root",
                item: "mc-accordion-item",
                control: "mc-accordion-control",
                label: "mc-accordion-label",
                chevron: "mc-accordion-chevron",
                panel: "mc-accordion-panel",
                content: "mc-accordion-content",
              }}
            >
              {getFilteredSubTasks().map((sub, gi) =>
                sub.fields && sub.fields.length > 0 ? (
                  <Accordion.Item key={gi} value={String(gi)}>
                    <Accordion.Control
                      icon={
                        <span className="mc-form-group-icon">
                          <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M9 11l3 3L22 4" />
                            <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
                          </svg>
                        </span>
                      }
                    >
                      <span className="mc-accordion-label-inner">
                        <span className="mc-form-group-title">
                          {sub?.task?.name}
                        </span>
                        <span className="mc-form-group-count">
                          {sub.fields.length}
                        </span>
                      </span>
                    </Accordion.Control>
                    <Accordion.Panel>
                      <div className="mc-form-checks">
                        {sub.fields.map((field, fi) => {
                          const fieldId = field._id;
                          const isChecked = checkedFieldIds.has(fieldId);
                          const textVal = fieldTexts[fieldId] || "";
                          const isRequired =
                            requiredCheckedFields.includes(fieldId);
                          const showError = isRequired && textVal.trim() === "";
                          return (
                            <div
                              key={fi}
                              className={`mc-check-item${isChecked ? " mc-check-item--checked" : ""}${showError ? " mc-check-item--error" : ""}`}
                            >
                              <div
                                className="mc-check-row"
                                onClick={() => toggleCheck(fieldId)}
                              >
                                <span className="mc-check-box">
                                  {isChecked && (
                                    <LuCheck
                                      size={12}
                                      color="#fff"
                                      strokeWidth={2.5}
                                    />
                                  )}
                                </span>
                                <span className="mc-check-name">
                                  {field.name}
                                </span>
                              </div>
                              {isChecked && (
                                <textarea
                                  className={`mc-check-textfield${showError ? " mc-check-textfield--error" : ""}`}
                                  placeholder="Add a note for this task..."
                                  value={textVal}
                                  onChange={(e) =>
                                    updateCheckText(fieldId, e.target.value)
                                  }
                                  rows={2}
                                />
                              )}
                              {showError && (
                                <span className="mc-check-error-msg">
                                  Required
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </Accordion.Panel>
                  </Accordion.Item>
                ) : null,
              )}
            </Accordion>
          )}

        {/* Comments */}
        {!showComment ? (
          <button
            type="button"
            className="mc-comment-add-btn"
            onClick={() => setShowComment(true)}
          >
            <LuMessageSquare size={15} />
            Add comment
            {taskTypeDetails?.commentRequired && (
              <span className="mc-form-label-req"> *</span>
            )}
          </button>
        ) : (
          <div className="mc-comment-box">
            <div className="mc-comment-box-header">
              <span className="mc-comment-box-title">Comment</span>
              <button
                type="button"
                className="mc-comment-box-close"
                onClick={() => {
                  setShowComment(false);
                  setComments([]);
                }}
                aria-label="Close comment"
              >
                <LuX size={12} strokeWidth={2.5} />
              </button>
            </div>
            <textarea
              className="mc-comment-textarea"
              placeholder="Add a comment or note for this task..."
              value={comments[0]?.text || comments[0] || ""}
              onChange={(e) =>
                setComments(e.target.value ? [e.target.value] : [])
              }
              rows={3}
              autoFocus
            />
          </div>
        )}
      </div>

      {/* ── Footer ──────────────────────────────────────────────────────── */}
      <div className="mc-form-footer">
        <button
          className="mc-form-cancel-btn"
          type="button"
          onClick={() => onTabSwitch("facesheet")}
          disabled={formLoading}
        >
          Cancel
        </button>
        <button
          className="mc-form-save-btn"
          type="button"
          onClick={handleSave}
          disabled={!taskType || formLoading}
        >
          {formLoading ? "Saving…" : "Save task"}
        </button>
      </div>
    </div>
  );
};

// ── Note Tab ───────────────────────────────────────────────────────────────

const NoteTab = ({ clientId, clientName, clientDetails, onTabSwitch }) => {
  const { allNotesTypes } = useSelector((s) => s.note);
  const { profile } = useSelector((s) => s?.profile);

  useEffect(() => {
    getAllNoteTypes();
    getAllFacilitiesModal("all");
    getAllPatientsModal("all");
    getAllStaff("all");
    getAllPlans(clientId, false);
  }, [clientId]);

  return (
    <AddNoteForm
      clientId={clientId}
      clientName={clientName}
      clientDetails={clientDetails}
      noteTypes={noteTypesForUser(allNotesTypes?.ntypes, profile)}
      onTabSwitch={onTabSwitch}
    />
  );
};

// ── Add Note Form ──────────────────────────────────────────────────────────

const AddNoteForm = ({
  clientId,
  clientName,
  clientDetails,
  noteTypes,
  onTabSwitch,
}) => {
  const { profile } = useSelector((s) => s.profile);
  const allPlans = useSelector((s) => s.supportPlan?.allPlans) || [];
  const rawFacilities = useSelector((s) => s.facility?.allFacilities);
  const allFacilities = Array.isArray(rawFacilities)
    ? rawFacilities
    : rawFacilities?.facilities || rawFacilities?.data || [];
  const { staff: staffStore } = useSelector((s) => s.staff);
  const { allPatients } = useSelector((s) => s.patient);
  const usageData = useSelector((s) => s.skai?.usageHistory);
  const { allSignature } = useSelector((s) => s?.signature) || {};

  const staffId = profile?.user?.id || profile?.user?._id || "";
  const userType = profile?.user?.type || profile?.user?.role || "";

  const clientFacilityId =
    clientDetails?.facility?._id ||
    (typeof clientDetails?.facility === "string"
      ? clientDetails.facility
      : null) ||
    clientDetails?.form2?.facility?._id ||
    (typeof clientDetails?.form2?.facility === "string"
      ? clientDetails.form2.facility
      : null) ||
    "";

  // ── AI access (mirrors NewNoteDesignCopy exactly) ───────────────────────────
  const usageArray = Array.isArray(usageData) ? usageData : [];
  const existingRecord = usageArray.find(
    (item) =>
      item?.admin === profile?.user?.admin || item?.admin === profile?.user?.id,
  );
  const facilityDetails = allFacilities.find(
    (f) => String(f?._id || f?.id) === String(clientFacilityId),
  );
  const staffDetails = staffStore?.staffs?.find(
    (s) => String(s?.id || s?._id) === String(staffId),
  );
  const isAiAccess =
    !profile?.user?.role && !profile?.user?.type
      ? true
      : facilityDetails &&
        (facilityDetails?.skaiAccess === true ||
          facilityDetails?.skaiAccess === undefined) &&
        staffDetails &&
        (staffDetails?.permissions?.aiAccess === true ||
          !staffDetails?.permissions);

  const creditLabel =
    (existingRecord?.planType === "monthly-subscription" &&
      existingRecord?.active) ||
    existingRecord?.planIsUnlimited
      ? "Unlimited"
      : existingRecord?.adminTotalTokens != null
        ? String(Number(existingRecord?.adminTotalTokens))
        : null;

  useEffect(() => {
    getSkaiTokenUsage(profile?.user?.admin || profile?.user?.id);
  }, [profile?.user?.admin, profile?.user?.id]);

  useEffect(() => {
    getAllSignatures();
  }, []);

  // ── Core state ─────────────────────────────────────────────────────────────
  const [noteTypeObj, setNoteTypeObj] = useState(null);
  const [formValues, setFormValues] = useState({});
  const [selectedFacility, setSelectedFacility] = useState("");
  const [selectedStaff, setSelectedStaff] = useState(staffId);

  // Group Note / Incident Report (mirrors desktop NewNoteDesignCopy)
  const [selectedGroupClients, setSelectedGroupClients] = useState([]);
  const [incidentNoteData, setIncidentNoteData] = useState(initialIncidentNoteData);

  // Special toggles
  const [supportPlan, setSupportPlan] = useState("no");
  const [descriptions, setDescriptions] = useState([mkInitialDesc()]);
  const [openedAccordion, setOpenedAccordion] = useState("0");
  const [essentialSupportToggle, setEssentialSupportToggle] = useState("no");
  const [essentialSupport, setEssentialSupport] = useState([mkInitialES()]);
  const [essentialAccordion, setEssentialAccordion] = useState("0");
  const [interventionPlanToggle, setInterventionPlanToggle] = useState("no");
  const [goals, setGoals] = useState([mkInitialGoal()]);
  const [goalsAccordion, setGoalsAccordion] = useState("0");
  const [goalItems, setGoalItems] = useState([
    [{ id: Date.now(), objectives: "", Intervention_Instruction: "" }],
  ]);

  // Plan data
  const [outComeTitle, setOutComeTitle] = useState([]);
  const [outcomeNumbers, setOutComeNumbers] = useState([]);
  const [essentialSupportTypeState, setEssentialSupportTypeState] = useState(
    [],
  );
  const [essentialSupportCount, setEssentialSupportCount] = useState([]);
  // Per-entry arrays so multiple ES entries don't share the same options
  const [frequencyOptions, setFrequencyOptions] = useState([[]]); // [[opts for entry 0], [opts for entry 1], ...]
  const [activities, setActivities] = useState([[]]); // [[acts for entry 0], ...]
  const [essentialNumbers, setEssentialNumbers] = useState([[]]); // [[outcome# opts for entry 0], ...]
  const [goalOptions, setGoalOptions] = useState([]);
  const [objectivesByGoal, setObjectivesByGoal] = useState([]);
  const [interventionsByGoal, setInterventionsByGoal] = useState([]);

  // Client data
  const [medicationOptions, setMedicationOptions] = useState([]);
  const [supportOptions, setSupportOptions] = useState([]);

  // AI / Voice
  const [isListening, setIsListening] = useState(false);
  const [listeningFor, setListeningFor] = useState(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const timerRef = useRef(null);
  // per-field AI preview: { [fieldKey]: { text, loading, showButtons } }
  const [aiPreviews, setAiPreviews] = useState({});

  // Form
  const [formLoading, setFormLoading] = useState(false);
  const [draftStatus, setDraftStatus] = useState("idle");
  const [errors, setErrors] = useState({});

  // Draft restore
  const [pendingDraft, setPendingDraft] = useState(null);
  const [draftFetching, setDraftFetching] = useState(false);
  const draftRestoredRef = useRef(false);
  const draftRestoringRef = useRef(false);

  // Pending approval modal
  const [pendingModal, setPendingModal] = useState(false);

  // Per-entry ES validation errors
  const [essentialSupportErrors, setEssentialSupportErrors] = useState([]);

  const isProgressNote = noteTypeObj?.name === "Progress Note";
  const isEssentialNote = noteTypeObj?.id === Essential_Support_Note;
  const isInterventionNote = noteTypeObj?.id === Intervention_Progress_Note;
  const isBillingServices = noteTypeObj?.id === Billing_Services_Note;
  const isGroupNote = noteTypeObj?.id === Group_Note;
  const isIncidentReport = noteTypeObj?.name === "Incident Report";

  const [serviceNumber, setServiceNumber] = useState(null);

  // Mirrors desktop noteTypeApproval: check adminApprovals first, then approvalRequired
  const noteTypeApproval = noteTypeObj
    ? noteTypeObj?.adminApprovals?.find(
        (item) => item?.admin === profile?.user?.admin,
      )
      ? noteTypeObj?.adminApprovals?.find(
          (item) => item?.admin === profile?.user?.admin,
        )?.status
      : noteTypeObj?.approvalRequired
    : false;

  // ── Speech-to-text ──────────────────────────────────────────────────────────
  const { startListening, stopListening } = useSpeechToText((transcript) => {
    if (!listeningFor) return;
    if (listeningFor.startsWith("main-")) {
      const fieldName = listeningFor.slice(5);
      setFV(fieldName, transcript);
    } else if (listeningFor.startsWith("desc-")) {
      const idx = parseInt(listeningFor.split("-")[1]);
      setDescriptions((prev) =>
        prev.map((d, i) => (i === idx ? { ...d, description: transcript } : d)),
      );
    } else if (listeningFor.startsWith("es-")) {
      const idx = parseInt(listeningFor.split("-")[1]);
      setEssentialSupport((prev) =>
        prev.map((e, i) => (i === idx ? { ...e, description: transcript } : e)),
      );
    } else if (listeningFor.startsWith("goal-behavior-")) {
      const idx = parseInt(listeningFor.split("goal-behavior-")[1]);
      setGoals((prev) =>
        prev.map((g, i) =>
          i === idx ? { ...g, behaviorDescription: transcript } : g,
        ),
      );
    } else if (listeningFor.startsWith("goal-response-")) {
      const idx = parseInt(listeningFor.split("goal-response-")[1]);
      setGoals((prev) =>
        prev.map((g, i) =>
          i === idx ? { ...g, responseDescription: transcript } : g,
        ),
      );
    } else if (listeningFor.startsWith("goal-plan-")) {
      const idx = parseInt(listeningFor.split("goal-plan-")[1]);
      setGoals((prev) =>
        prev.map((g, i) =>
          i === idx ? { ...g, planDescription: transcript } : g,
        ),
      );
    }
  });

  const toggleListening = (fieldKey) => {
    if (isListening && listeningFor === fieldKey) {
      // Confirm — keep transcript, stop recording
      stopListening();
      clearInterval(timerRef.current);
      setRecordingTime(0);
      setIsListening(false);
      setListeningFor(null);
    } else {
      if (isListening) {
        stopListening();
        clearInterval(timerRef.current);
      }
      setListeningFor(fieldKey);
      setIsListening(true);
      setRecordingTime(0);
      timerRef.current = setInterval(
        () => setRecordingTime((t) => t + 1),
        1000,
      );
      startListening();
    }
  };

  const handleMicCancel = (fieldKey) => {
    stopListening();
    clearInterval(timerRef.current);
    setRecordingTime(0);
    setIsListening(false);
    setListeningFor(null);
    // Clear the field transcript on cancel (desktop behaviour)
    applyFieldValue(fieldKey, "");
  };

  const handleMicRedo = (fieldKey) => {
    stopListening();
    clearInterval(timerRef.current);
    applyFieldValue(fieldKey, "");
    setRecordingTime(0);
    timerRef.current = setInterval(() => setRecordingTime((t) => t + 1), 1000);
    startListening();
  };

  const applyFieldValue = (fieldKey, val) => {
    if (fieldKey.startsWith("main-")) setFV(fieldKey.slice(5), val);
    else if (fieldKey.startsWith("desc-")) {
      const idx = parseInt(fieldKey.split("-")[1]);
      setDescriptions((prev) =>
        prev.map((d, i) => (i === idx ? { ...d, description: val } : d)),
      );
    } else if (fieldKey.startsWith("es-")) {
      const idx = parseInt(fieldKey.split("-")[1]);
      setEssentialSupport((prev) =>
        prev.map((e, i) => (i === idx ? { ...e, description: val } : e)),
      );
    } else if (fieldKey.startsWith("goal-behavior-")) {
      const idx = parseInt(fieldKey.split("goal-behavior-")[1]);
      setGoals((prev) =>
        prev.map((g, i) =>
          i === idx ? { ...g, behaviorDescription: val } : g,
        ),
      );
    } else if (fieldKey.startsWith("goal-response-")) {
      const idx = parseInt(fieldKey.split("goal-response-")[1]);
      setGoals((prev) =>
        prev.map((g, i) =>
          i === idx ? { ...g, responseDescription: val } : g,
        ),
      );
    } else if (fieldKey.startsWith("goal-plan-")) {
      const idx = parseInt(fieldKey.split("goal-plan-")[1]);
      setGoals((prev) =>
        prev.map((g, i) => (i === idx ? { ...g, planDescription: val } : g)),
      );
    }
  };

  // ── Helpers ────────────────────────────────────────────────────────────────
  const setFV = (key, value) =>
    setFormValues((prev) => ({ ...prev, [key]: value }));

  const isClientField = (f) =>
    f?.fieldType?.name === "Clients" ||
    f?.field === "Client Name" ||
    f?.field === "Patient Name";

  const isStaffField = (f) => f?.fieldType?.name === "Staff";
  const isFacilityField = (f) => f?.fieldType?.name === "Facilities";

  // Exact same as desktop objectToString — capitalises first letter, shows "-" for missing
  const objectToString = (obj) => {
    return Object.entries(obj || {})
      .map(
        ([key, value]) =>
          `${key.charAt(0).toUpperCase() + key.slice(1)}: "${value || "-"}"`,
      )
      .join("\n");
  };

  // Exact same as desktop arrayToString
  const arrayToString = (array) => {
    if (!array?.length) return "";
    return array
      .filter((v) => v)
      .map((v, i) => `${i + 1}) ${v}`)
      .join("\n \n");
  };

  // Exact same as desktop filterNonNullFields
  const filterNonNullFields = (obj) => {
    const result = {};
    for (const [key, value] of Object.entries(obj || {})) {
      if (value !== null && value !== undefined && value !== "") {
        result[key] = value;
      }
    }
    return result;
  };

  // ── Note type change ───────────────────────────────────────────────────────
  const handleNoteTypeChange = (typeId) => {
    const nt = noteTypes.find((t) => (t._id || t.id) === typeId);
    setNoteTypeObj(nt || null);

    const now = new Date();
    const todayStr = now.toISOString().slice(0, 10);
    const currentTime = `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;

    const initVals = {};
    (nt?.custom || []).forEach((f) => {
      const typeName = f.fieldType?.name;
      if (typeName === "date" || typeName === "Date") {
        initVals[f.field] = todayStr;
      } else if (typeName === "time" || typeName === "Time") {
        initVals[f.field] = currentTime;
      } else {
        initVals[f.field] = "";
      }
    });

    // Pre-fill
    const clientField = (nt?.custom || []).find((f) => isClientField(f));
    if (clientField) initVals[clientField.field] = clientId;
    const staffField = (nt?.custom || []).find((f) => isStaffField(f));
    if (staffField) initVals[staffField.field] = staffId;
    const facField = (nt?.custom || []).find((f) => isFacilityField(f));
    if (facField) initVals[facField.field] = clientFacilityId;

    const patient = allPatients?.find(
      (p) => p.id === clientId || p._id === clientId,
    );
    if (patient) {
      initVals["Medication Records"] = "";
      initVals["Support Plan"] = "";

      // Mirror desktop handlePatientChange — populate patient-derived fields
      const pcpInfo = {
        "PCP Name": patient?.form2?.pcpName,
        "PCP Email": patient?.form2?.pcpEmail,
        "PCP Phone Number": patient?.form2?.pcpPhoneNumber,
        "PCP Fax Number": patient?.form2?.pcpFaxNumber,
      };
      const insuranceDetails = {
        "Insurance Company Name": patient?.form2?.insuranceCompanyName,
        "Member Name": patient?.form2?.memberName,
        "Member Number": patient?.form2?.memberNumber,
        "Prescription Group": patient?.form2?.prescriptionGroup,
        "Group Number": patient?.form2?.groupNumber,
        "Effective Date": formatDate(patient?.form2?.effectiveDate),
      };
      initVals["All PCP Info"] = objectToString(pcpInfo);
      initVals["Known Allergies"] = arrayToString(patient?.form1?.knownAllergies);
      initVals["Dietary Restrictions"] = arrayToString(patient?.form1?.dietaries);
      initVals["Known Illnes"] = arrayToString(
        patient?.form1?.knownIllness
          ? patient.form1.knownIllness.map(
              (v) => `${v?.illness || ""} - Contagious: "${v?.contagious || ""}"`,
            )
          : [],
      ) || "-";
      initVals["Insurance Details"] = objectToString(insuranceDetails);
      initVals["Known Wounds"] = patient?.form1?.woundsInformation;

      setMedicationOptions(
        Array.isArray(patient?.form3)
          ? patient.form3.map((v) => {
              const { isPrescribed, id, ...rest } = v;
              return {
                label: v.medicationName,
                value: objectToString({
                  ...filterNonNullFields(rest),
                  Prescribed: isPrescribed ? "Yes" : "No",
                }),
              };
            })
          : [],
      );
      // Support Plan options — same as desktop setSupportOptions in handlePatientChange
      setSupportOptions(
        (allPlans || [])
          .find(
            (v) => v?.client?._id === clientId || v?.client?.id === clientId,
          )
          ?.outcomes?.map(({ id: _id, ...rest }) => ({
            value: objectToString({ ...filterNonNullFields(rest) }),
            label: rest.outcomeTitle,
          })) || [],
      );
    }

    setFormValues(initVals);
    // Only set selectedFacility if this note type has a facility field and clientFacilityId is known
    // (mirrors desktop: outcomes only load after facility is selected/confirmed)
    setSelectedFacility(facField && clientFacilityId ? clientFacilityId : "");

    // Group Note — start with the current client already selected (mirrors
    // desktop's facility-driven selection, but this form is always scoped to
    // one client's chart so it makes sense to pre-include them).
    if (nt?.id === Group_Note) {
      setSelectedGroupClients(clientId ? [clientId] : []);
    } else {
      setSelectedGroupClients([]);
    }

    // Incident Report — pre-fill the client whose chart this was opened from
    // and the logged-in staff member, same as desktop's userId/profile prefill.
    if (nt?.name === "Incident Report") {
      setIncidentNoteData({
        ...initialIncidentNoteData,
        clientsInvolved: clientId ? [clientId] : [],
        staffInvolved: staffId ? [staffId] : [],
      });
    } else {
      setIncidentNoteData(initialIncidentNoteData);
    }

    setSupportPlan("no");
    setServiceNumber(null);
    setDescriptions([mkInitialDesc()]);
    setEssentialSupportToggle("no");
    setEssentialSupport([mkInitialES()]);
    setFrequencyOptions([[]]);
    setActivities([[]]);
    setEssentialNumbers([[]]);
    setInterventionPlanToggle("no");
    setGoals([mkInitialGoal()]);
    setGoalItems([
      [{ id: Date.now(), objectives: "", Intervention_Instruction: "" }],
    ]);
    setOutComeTitle([]);
    setOutComeNumbers([]);
    setEssentialSupportTypeState([]);
    setEssentialSupportCount([]);
    setGoalOptions([]);
    setObjectivesByGoal([]);
    setInterventionsByGoal([]);
    setErrors({});
    setAiPreviews({});
  };

  // ── Plan data loading — mirrors desktop useEffect([selectedPatient, allPlans, selectedFacility, noteType]) ──
  useEffect(() => {
    if (!noteTypeObj) return;

    let outcomeTitleArr = [];
    let outcomeNumArr = [];
    let esTypeArr = [];
    let esCountArr = [];
    let goalTitleArr = [];

    const isIntervention = noteTypeObj?.name === "Intervention Progress Note";

    (allPlans || [])
      .filter(
        (v) =>
          (v?.client?._id === clientId || v?.client?.id === clientId) &&
          v?.facility?._id === selectedFacility &&
          v?.status === "active" &&
          v?.isActive === true &&
          v?.deleted !== true,
      )
      .forEach((v) => {
        if (isIntervention) {
          // Goals for Intervention Progress Note
          (v?.goals || []).forEach((g) => {
            goalTitleArr.push({
              value: g.Identified_Relevant_Attainable_Goals,
              label: g.Identified_Relevant_Attainable_Goals,
              disabled: g.hasOwnProperty("isActive") ? !g.isActive : false,
            });
          });
        } else {
          // Outcomes for Progress Note / Essential Support Note
          (v?.outcomes || []).forEach((vv) => {
            outcomeTitleArr.push({
              value: vv.id,
              label: vv.outcomeTitle,
              disabled: vv.hasOwnProperty("isActive") ? !vv.isActive : false,
            });
            outcomeNumArr.push({
              value: vv.id,
              label: vv.outcomeNumber,
              disabled: vv.hasOwnProperty("isActive") ? !vv.isActive : false,
            });
          });

          // Essential Support types + activities count
          (v?.essentialSupport || []).forEach((es) => {
            esTypeArr.push({
              value: es.essentialSupportType,
              label: es.essentialSupportType,
            });
            esCountArr.push(...(es.activities || []));
          });
        }
      });

    setOutComeTitle([
      ...new Map(outcomeTitleArr.map((o) => [o.value, o])).values(),
    ]);
    setOutComeNumbers([
      ...new Map(outcomeNumArr.map((o) => [o.value, o])).values(),
    ]);
    setEssentialSupportTypeState([
      ...new Map(esTypeArr.map((o) => [o.value, o])).values(),
    ]);
    setGoalOptions(goalTitleArr);
    setEssentialSupportCount(esCountArr);
  }, [allPlans, noteTypeObj, selectedFacility, clientId]);

  useEffect(() => {
    setSelectedStaff(staffId);
  }, [staffId]);

  // Mirrors desktop: restore serviceNumber when editing a Billing Services note
  useEffect(() => {
    if (isBillingServices && clientDetails && formValues["Service Type"]) {
      const found = (clientDetails?.form1?.services || []).find(
        // eslint-disable-next-line eqeqeq
        (s) => s?.servicenumber == formValues["Service Type"],
      );
      setServiceNumber(found || null);
    }
  }, [clientDetails, isBillingServices, formValues["Service Type"]]);

  // ── Draft fetch whenever note type changes ─────────────────────────────────
  useEffect(() => {
    if (!noteTypeObj?.id || !clientId || !clientFacilityId) return;
    setPendingDraft(null);
    setDraftFetching(true);
    getNoteDraft(noteTypeObj.id, clientId, clientFacilityId)
      .then((draft) => {
        if (
          draft?.formState?.fields &&
          Object.keys(draft.formState.fields).length > 2
        ) {
          setPendingDraft(draft.formState);
        }
        setDraftFetching(false);
      })
      .catch(() => setDraftFetching(false));
  }, [noteTypeObj?.id]);

  // ── Restore draft ───────────────────────────────────────────────────────────
  const restoreDraft = () => {
    if (!pendingDraft) return;
    const fs = { ...pendingDraft };
    setPendingDraft(null);
    if (fs.supportPlan) setSupportPlan(fs.supportPlan);
    if (fs.essentialSupportToggle)
      setEssentialSupportToggle(fs.essentialSupportToggle);
    if (fs.interventionPlanToggle)
      setInterventionPlanToggle(fs.interventionPlanToggle);
    if (fs.descriptions?.length > 0) setDescriptions(fs.descriptions);
    if (fs.essentialSupport?.length > 0)
      setEssentialSupport(fs.essentialSupport);
    if (fs.goals?.length > 0) setGoals(fs.goals);
    if (fs.goalItems?.length > 0) setGoalItems(fs.goalItems);
    if (fs.outComeTitle?.length > 0) setOutComeTitle(fs.outComeTitle);
    if (fs.outcomeNumbers?.length > 0) setOutComeNumbers(fs.outcomeNumbers);
    if (fs.essentialSupportCount?.length > 0)
      setEssentialSupportCount(fs.essentialSupportCount);
    if (fs.essentialSupportTypeState?.length > 0)
      setEssentialSupportTypeState(fs.essentialSupportTypeState);
    if (fs.frequencyOptions?.length > 0)
      setFrequencyOptions(fs.frequencyOptions);
    if (fs.activities?.length > 0) setActivities(fs.activities);
    if (fs.essentialNumbers?.length > 0)
      setEssentialNumbers(fs.essentialNumbers);
    if (fs.goalOptions?.length > 0) setGoalOptions(fs.goalOptions);
    if (fs.objectivesByGoal?.length > 0)
      setObjectivesByGoal(fs.objectivesByGoal);
    if (fs.interventionsByGoal?.length > 0)
      setInterventionsByGoal(fs.interventionsByGoal);
    const saved = {
      goals: fs.goals,
      goalItems: fs.goalItems,
      descriptions: fs.descriptions,
      essentialSupport: fs.essentialSupport,
    };
    setTimeout(() => {
      draftRestoringRef.current = true;
      if (saved.goals?.length > 0) setGoals(saved.goals);
      if (saved.goalItems?.length > 0) setGoalItems(saved.goalItems);
      if (saved.descriptions?.length > 0) setDescriptions(saved.descriptions);
      if (saved.essentialSupport?.length > 0)
        setEssentialSupport(saved.essentialSupport);
    }, 800);
    if (fs.fields) setFormValues((prev) => ({ ...prev, ...fs.fields }));
    if (fs.selectedFacility) setSelectedFacility(fs.selectedFacility);
    setDraftStatus("restored");
    setTimeout(() => setDraftStatus("idle"), 3000);
  };

  // ── handleEssentialSupportDesc — update goals or essentialSupport fields ───
  const handleEssentialSupportDesc = (supportId, field, value, indexx) => {
    if (isInterventionNote) {
      setGoals((prev) =>
        prev.map((item, index) =>
          index === indexx
            ? item.id === supportId
              ? { ...item, [field]: value }
              : item
            : item,
        ),
      );
    } else {
      setEssentialSupport((prev) =>
        prev.map((item, index) =>
          index === indexx
            ? item.id === supportId
              ? { ...item, [field]: value }
              : item
            : item,
        ),
      );
    }
  };

  // ── handleGoalActivityChange — auto-fill paired objective/instruction ───────
  const handleGoalActivityChange = (id, field, value, ai, goalIndex) => {
    const matchingActivities = (allPlans || [])
      .filter(
        (v) =>
          (v?.client?._id === clientId || v?.client?.id === clientId) &&
          v?.facility?._id === selectedFacility &&
          v?.status === "active" &&
          v?.isActive === true &&
          v?.deleted !== true,
      )
      .flatMap((v) => v?.goals || [])
      .flatMap((g) => g?.goalActivities || []);

    const newGoalItems = [...goalItems];
    newGoalItems[goalIndex] = (newGoalItems[goalIndex] || []).map((item) => {
      if (item.id !== id) return item;
      if (field === "objectives") {
        const sel = matchingActivities.find((ga) => ga?.objectives === value);
        return {
          ...item,
          objectives: value,
          // clear paired field to "" if not found — same as desktop
          Intervention_Instruction: sel?.Intervention_Instruction || "",
        };
      }
      if (field === "Intervention_Instruction") {
        const sel = matchingActivities.find(
          (ga) => ga?.Intervention_Instruction === value,
        );
        return {
          ...item,
          Intervention_Instruction: value,
          // clear paired field to "" if not found — same as desktop
          objectives: sel?.objectives || "",
        };
      }
      return { ...item, [field]: value };
    });
    setGoalItems(newGoalItems);
    setGoals((prev) =>
      prev.map((g, i) =>
        i === goalIndex ? { ...g, goalActivities: newGoalItems[goalIndex] } : g,
      ),
    );
  };

  // ── Staff signature (same as desktop) ─────────────────────────────────────
  const staffSignature = allSignature?.find(
    ({ staff, admin }) =>
      (staff && staff?.id === (formValues?.["Staff Name"] || selectedStaff)) ||
      (admin && admin?.id === (formValues?.["Staff Name"] || selectedStaff)),
  );

  const renderSignature = () => {
    if (!noteTypeObj) return null;
    if (
      !staffSignature?.signature &&
      !staffSignature?.signatureImage &&
      !staffSignature?.signatureText
    )
      return null;
    return (
      <div className="mc-signature-block">
        <div className="mc-signature-label">Staff Signature</div>
        <div className="mc-signature-box">
          {staffSignature?.signature || staffSignature?.signatureImage ? (
            <img
              src={
                decrypt(staffSignature?.signature) ||
                decrypt(staffSignature?.signatureImage)
              }
              alt="Staff Signature"
              className="mc-signature-img"
            />
          ) : staffSignature?.signatureText ? (
            <span className="mc-signature-text">
              {staffSignature.signatureText}
            </span>
          ) : null}
        </div>
      </div>
    );
  };

  // ── Essential support handlers — mirrors desktop NewNoteDesignCopy exactly ──

  const handleEsTypeChange = (idx, value) => {
    // Reset all downstream fields on type change (same as desktop handleEssentialTypeChange)
    setEssentialSupport((prev) =>
      prev.map((e, i) =>
        i === idx
          ? {
              ...e,
              essentialSupportType: value,
              howOftenEssentialSupport: "",
              essentialNumber: "",
              essentialActivityNumber: "",
            }
          : e,
      ),
    );

    const matchingSupport = (allPlans || [])
      .filter(
        (v) =>
          (v?.client?._id === clientId || v?.client?.id === clientId) &&
          v?.facility?._id === selectedFacility &&
          v?.status === "active" &&
          v?.isActive === true &&
          v?.deleted !== true,
      )
      .flatMap((v) => v?.essentialSupport || [])
      .filter((es) => es?.essentialSupportType === value);

    // Frequencies: check inside activities first, then top-level (desktop logic)
    const allFreqs = matchingSupport.flatMap((s) => {
      const actFreqs = (s?.activities || [])
        .map((a) => a?.howOftenEssentialSupport)
        .filter(Boolean);
      if (actFreqs.length > 0) return actFreqs;
      return s?.howOftenEssentialSupport ? [s.howOftenEssentialSupport] : [];
    });
    const formattedFreqs = [...new Set(allFreqs)].map((f) => ({
      label: f,
      value: f,
    }));

    // All activities for this type (desktop sets full list on type change)
    const allActs = matchingSupport.flatMap((s) => s?.activities || []);
    const formattedActs = allActs.map((a) => ({
      supportStatement: a.supportStatement,
      id: a.id,
    }));

    // Update per-index options arrays
    setFrequencyOptions((prev) => {
      const n = [...prev];
      n[idx] = formattedFreqs;
      return n;
    });
    setActivities((prev) => {
      const n = [...prev];
      n[idx] = formattedActs;
      return n;
    });
    setEssentialNumbers((prev) => {
      const n = [...prev];
      n[idx] = [];
      return n;
    });
  };

  const handleEsFrequencyChange = (idx, value) => {
    const selectedType = essentialSupport[idx]?.essentialSupportType;

    // Keep essentialNumber (desktop does NOT reset it on frequency change)
    setEssentialSupport((prev) =>
      prev.map((e, i) =>
        i === idx
          ? {
              ...e,
              howOftenEssentialSupport: value,
              essentialNumber: e.essentialNumber, // preserve — desktop keeps it
              essentialActivityNumber: "", // reset activity
            }
          : e,
      ),
    );

    const matchingSupport = (allPlans || [])
      .filter(
        (v) =>
          (v?.client?._id === clientId || v?.client?.id === clientId) &&
          v?.facility?._id === selectedFacility &&
          v?.status === "active" &&
          v?.isActive === true &&
          v?.deleted !== true,
      )
      .flatMap((v) => v?.essentialSupport || [])
      .filter((es) => es?.essentialSupportType === selectedType);

    // Filter activities by frequency (mirrors desktop handleEssentialFrequencyChange)
    let relatedActivities = [];
    matchingSupport.forEach((item) => {
      const found = (item?.activities || []).filter(
        (a) =>
          a?.howOftenEssentialSupport === value &&
          item?.essentialSupportType === selectedType,
      );
      if (found.length) {
        relatedActivities.push(...found);
      } else if (
        item?.howOftenEssentialSupport === value &&
        item?.essentialSupportType === selectedType
      ) {
        relatedActivities.push({
          ...item,
          id: item.activities?.[0]?.id || item.id,
          supportStatement:
            item.activities?.[0]?.supportStatement || item.supportStatement,
        });
      }
    });
    relatedActivities = relatedActivities.filter(
      (a, i, s) => i === s.findIndex((b) => b.id === a.id),
    );

    // Outcome # options for this frequency (mirrors desktop)
    const filteredForNums = matchingSupport.filter((_es) => {
      const hasActivityMatch = _es?.activities?.some(
        (act) =>
          act?.howOftenEssentialSupport === value &&
          _es?.essentialSupportType === selectedType,
      );
      return hasActivityMatch || _es?.howOftenEssentialSupport === value;
    });
    const outcomeNums = Array.from(
      new Set(filteredForNums.map((s) => s?.essentialNumber).filter(Boolean)),
    ).map((n) => ({ label: n, value: n }));

    setActivities((prev) => {
      const n = [...prev];
      n[idx] = relatedActivities;
      return n;
    });
    setEssentialNumbers((prev) => {
      const n = [...prev];
      n[idx] = outcomeNums;
      return n;
    });
  };

  // Activity selected — auto-fill frequency + essentialNumber (mirrors desktop handleEssentialChange)
  const handleEsActivityChange = (idx, value) => {
    const filteredPlans = (allPlans || []).filter(
      (v) =>
        (v?.client?._id === clientId || v?.client?.id === clientId) &&
        v?.facility?._id === selectedFacility &&
        v?.status === "active" &&
        v?.isActive === true &&
        v?.deleted !== true,
    );

    // Find the ES plan entry that owns this activity
    // Use == (loose) to handle id type mismatch (number vs string from select)
    const selectedSupport = filteredPlans
      .flatMap((plan) => plan.essentialSupport || [])
      // eslint-disable-next-line eqeqeq
      .find((es) => es?.activities?.some((a) => a.id == value));

    // eslint-disable-next-line eqeqeq
    const selectedActivity = selectedSupport?.activities?.find(
      (a) => a.id == value,
    );

    // Frequencies from the owning support (desktop logic)
    const allFrequencies = (() => {
      if (!selectedSupport) return [];
      const actFreqs = (selectedSupport.activities || [])
        .map((a) => a?.howOftenEssentialSupport)
        .filter(Boolean);
      if (actFreqs.length > 0) return actFreqs;
      return selectedSupport?.howOftenEssentialSupport
        ? [selectedSupport.howOftenEssentialSupport]
        : [];
    })();
    const formattedFreqs = [...new Set(allFrequencies)].map((f) => ({
      label: f,
      value: f,
    }));
    setFrequencyOptions((prev) => {
      const n = [...prev];
      n[idx] = formattedFreqs;
      return n;
    });

    // Outcome # options — all ES numbers across all plans (desktop logic)
    const esNums = Array.from(
      new Set(
        filteredPlans
          .flatMap((v) => v?.essentialSupport || [])
          .map((item) => item?.essentialNumber)
          .filter(Boolean),
      ),
    ).map((n) => ({ label: n, value: n }));
    setEssentialNumbers((prev) => {
      const n = [...prev];
      n[idx] = esNums;
      return n;
    });

    // Always set howOftenEssentialSupport from activity (desktop always overwrites, no ||)
    const finalHowOften =
      selectedActivity?.howOftenEssentialSupport ||
      selectedSupport?.howOftenEssentialSupport ||
      "";

    // Spread ...selectedSupport into entry (desktop does this) to bring in activities array
    setEssentialSupport((prev) =>
      prev.map((e, i) => {
        if (i !== idx) return e;
        return {
          ...e,
          ...(selectedSupport || {}), // brings in activities array like desktop
          essentialActivityNumber: value,
          howOftenEssentialSupport: finalHowOften, // always overwrite — desktop behaviour
          essentialNumber:
            selectedSupport?.essentialNumber || e.essentialNumber,
        };
      }),
    );
  };

  // ── Goal handlers ──────────────────────────────────────────────────────────
  const handleGoalChange = (idx, value) => {
    setGoals((prev) =>
      prev.map((g, i) =>
        i === idx ? { ...g, identifiedGoal: value, goalNumber: "" } : g,
      ),
    );
    const matchingGoal = (allPlans || [])
      .filter(
        (v) =>
          (v?.client?._id === clientId || v?.client?.id === clientId) &&
          v?.facility?._id === selectedFacility &&
          v?.status === "active" &&
          v?.isActive === true &&
          v?.deleted !== true,
      )
      .flatMap((v) => v?.goals || [])
      .filter((g) => g?.Identified_Relevant_Attainable_Goals === value);

    setObjectivesByGoal(
      matchingGoal.flatMap((g) =>
        (g?.goalActivities || []).map((ga) => ({
          label: ga.objectives,
          value: ga.objectives,
        })),
      ),
    );
    setInterventionsByGoal(
      matchingGoal.flatMap((g) =>
        (g?.goalActivities || []).map((ga) => ({
          label: ga.Intervention_Instruction,
          value: ga.Intervention_Instruction,
        })),
      ),
    );
    const nums = [
      ...new Set(matchingGoal.map((g) => g?.goalNumber).filter(Boolean)),
    ];
    if (nums.length > 0) {
      setGoals((prev) =>
        prev.map((g, i) => (i === idx ? { ...g, goalNumber: nums[0] } : g)),
      );
    }
    const newGoalItems = [...goalItems];
    newGoalItems[idx] = [
      { id: Date.now(), objectives: "", Intervention_Instruction: "" },
    ];
    setGoalItems(newGoalItems);
  };

  // ── Outcome change ─────────────────────────────────────────────────────────
  const handleOutcomeChange = (idx, field, value) => {
    const strVal = String(value);

    // Both outComeTitle and outcomeNumbers use outcome ID as .value
    // When title is selected → auto-fill number with same ID, and vice-versa
    const pairedArray =
      field === "outcomeTitle" ? outcomeNumbers : outComeTitle;
    const pairedObj = pairedArray.find((o) => String(o.value) === strVal);

    let rest = {};
    let choosenPlan = null;
    if (field === "outcomeTitle" || field === "outcomeNumber") {
      const plan = (allPlans || [])
        .filter(
          (v) =>
            (v?.client?._id === clientId || v?.client?.id === clientId) &&
            v?.facility?._id === selectedFacility &&
            v?.status === "active" &&
            v?.isActive === true &&
            v?.deleted !== true,
        )
        .find((v) =>
          (v?.outcomes || []).find((oc) => String(oc?.id) === strVal),
        );
      if (plan) {
        choosenPlan = plan;
        const { id: _id, ...others } =
          plan.outcomes.find((o) => String(o.id) === strVal) || {};
        rest = Object.fromEntries(
          Object.entries(others).filter(([, v]) => v != null && v !== ""),
        );
      }
    }

    setDescriptions((prev) => {
      const alreadySelected = prev.some(
        (d, i) =>
          i !== idx &&
          (String(d.outcomeTitle) === strVal ||
            String(d.outcomeNumber) === strVal),
      );
      return prev.map((d, i) => {
        if (i !== idx) return d;
        const updated = { ...d, [field]: strVal };
        if (field === "outcomeTitle" || field === "outcomeNumber") {
          // paired field: title ↔ number — both store same outcome ID
          updated[field === "outcomeTitle" ? "outcomeNumber" : "outcomeTitle"] =
            pairedObj ? String(pairedObj.value) : strVal;
          updated.outComeCategory = rest?.outcomeCategory || "";
          updated.details = rest;
          updated.planId = choosenPlan?.planType?.planId || "1";
          updated.error = alreadySelected ? "Already Selected" : null;
        }
        return updated;
      });
    });
  };

  // ── Get current text value for a fieldKey ─────────────────────────────────
  const getFieldCurrentValue = (fieldKey) => {
    if (fieldKey.startsWith("main-"))
      return formValues[fieldKey.slice(5)] || "";
    if (fieldKey.startsWith("desc-")) {
      const idx = parseInt(fieldKey.split("-")[1]);
      return descriptions[idx]?.description || "";
    }
    if (fieldKey.startsWith("es-")) {
      const idx = parseInt(fieldKey.split("-")[1]);
      return essentialSupport[idx]?.description || "";
    }
    if (fieldKey.startsWith("goal-behavior-")) {
      const idx = parseInt(fieldKey.split("goal-behavior-")[1]);
      return goals[idx]?.behaviorDescription || "";
    }
    if (fieldKey.startsWith("goal-response-")) {
      const idx = parseInt(fieldKey.split("goal-response-")[1]);
      return goals[idx]?.responseDescription || "";
    }
    if (fieldKey.startsWith("goal-plan-")) {
      const idx = parseInt(fieldKey.split("goal-plan-")[1]);
      return goals[idx]?.planDescription || "";
    }
    return "";
  };

  // ── Apply AI preview text to the actual field (Replace Text) ────────────
  const applyAiPreview = (fieldKey, generated) => {
    applyFieldValue(fieldKey, generated);
  };

  // ── buildAiContext: collects structured context for the backend prompt builder ──
  // Returns { action, context } — no prompt text lives in the frontend.
  const buildAiContext = (fieldKey, promptType = "default") => {
    const staffNote = getFieldCurrentValue(fieldKey).trim();
    const clientName = clientDetails?.name || "";
    const facilityName = facilityDetails?.name || "";
    const noteTypeName = noteTypeObj?.name || "";
    const selectedStateAbbr = clientDetails?.form1?.state || "";
    const staffName =
      staffStore?.staffs?.find(
        (s) => String(s?.id || s?._id) === String(staffId),
      )?.name || "";
    const shift = formValues?.Shift || "";
    const dateOfService = formValues?.Date
      ? moment(formValues.Date).format("MM/DD/YYYY")
      : "";

    // ── Support Activities section (same logic as desktop) ──
    let filteredDescriptions = descriptions?.filter(
      (item) => item?.details !== null,
    );
    let supportActivitiesSection = "";
    if (filteredDescriptions?.length > 0 && descriptions?.[0]?.details) {
      const { outcomeCategory, activities } = descriptions[0].details;
      const activityNames =
        activities
          ?.map((a) => a?.supportActivity)
          .filter(Boolean)
          .join(", ") || "";
      const supportInstructionsText =
        activities
          ?.map((a) => {
            const instr = a?.supportInstructions;
            if (Array.isArray(instr))
              return instr
                .map((i) => (typeof i === "string" ? i : i?.text))
                .filter(Boolean)
                .join(", ");
            if (typeof instr === "string") return instr;
            return "";
          })
          .filter(Boolean)
          .join(", ") || "";
      supportActivitiesSection =
        `${outcomeCategory || ""}\n${activityNames}\n${supportInstructionsText}`.trim();
    }

    // ── Essential Support section (same logic as desktop) ──
    let tempEssentialSupport = "";
    const selectedSupport = essentialSupport?.[0];
    if (selectedSupport) {
      const selectedActivity = selectedSupport?.activities?.find(
        (activity) =>
          String(activity?.id) ===
          String(selectedSupport?.essentialActivityNumber),
      );
      let howToSupportText = "";
      const howToSupport = selectedActivity?.howToSupport;
      if (Array.isArray(howToSupport)) {
        howToSupportText = howToSupport
          .map((item) => (typeof item === "string" ? item : item?.text))
          .filter(Boolean)
          .join(", ");
      } else if (typeof howToSupport === "string") {
        howToSupportText = howToSupport;
      }
      const supportStatement = selectedActivity?.supportStatement || "";
      tempEssentialSupport = `${supportStatement}\n${howToSupportText}`.trim();
    }

    // ── Intervention Plan section (same logic as desktop) ──
    const getInterventionPlan = (idx) => {
      const selectedIntervention = goals?.[idx];
      if (!selectedIntervention?.goalActivities?.length) return "";
      return selectedIntervention.goalActivities
        .map(
          (a) =>
            ` - Objectives : ${a?.objectives || ""}\n - Instructions : ${a?.Intervention_Instruction || ""}`,
        )
        .join("\n\n");
    };

    // ── Resolve fieldKey index for goal fields ──
    const getGoalIdx = () => {
      if (fieldKey.startsWith("goal-behavior-"))
        return parseInt(fieldKey.split("goal-behavior-")[1]);
      if (fieldKey.startsWith("goal-response-"))
        return parseInt(fieldKey.split("goal-response-")[1]);
      if (fieldKey.startsWith("goal-plan-"))
        return parseInt(fieldKey.split("goal-plan-")[1]);
      return 0;
    };

    // Determine action for the backend prompt builder
    const action =
      promptType === "behavior"  ? "mobile-behavior"
      : promptType === "response"  ? "mobile-response"
      : promptType === "plan"      ? "mobile-plan"
      : promptType === "polish"    ? "mobile-polish"
      : promptType === "rewrite"   ? "mobile-rewrite"
      : promptType === "elaborate" ? "mobile-elaborate"
      : "mobile-default";

    // Resolve intervention goal context for behavior/response/plan actions
    const goalIdx = getGoalIdx();
    const selectedIntervention = goals?.[goalIdx] || {};
    const tempInterventionPlan = getInterventionPlan(goalIdx);

    const context = {
      state: selectedStateAbbr,
      dateOfService,
      sessionType: "Individual Session",
      individualName: clientName,
      supportActivities: (supportActivitiesSection + " " + tempEssentialSupport).trim(),
      staffNote,
      staffName,
      shift,
      noteType: noteTypeName,
      facilityType: facilityName,
      behaviorInput: staffNote,
      responseInput: staffNote,
      planInput: staffNote,
      identifiedGoals: selectedIntervention?.identifiedGoal || "",
      interventionPlan: tempInterventionPlan,
    };

    return { action, context };
  };

  // ── Credit check (mirrors desktop checkTokenUsageWarning exactly) ───────────
  const checkCredits = () => {
    if (existingRecord?.active === false && existingRecord?.paid === false) {
      swal({
        title: "AI Note Assistant Credits Not Found",
        text: "You have not purchased any AI credits yet. Please visit the Settings page and navigate to the AI credits section to buy credits.",
        icon: "info",
        dangerMode: false,
      });
      return false;
    }
    if (existingRecord?.planType === "onetime-purchase") {
      const tokensUsage = existingRecord?.adminTotalTokens;
      const totalTokens = Number(tokensUsage?.adminTotalTokens);
      const usedTokens = Number(tokensUsage?.adminUsedTokens);
      const originalTotal = totalTokens + usedTokens;
      const pct =
        originalTotal > 0 ? Math.round((usedTokens / originalTotal) * 100) : 0;
      if (pct >= 100) {
        swal({
          title: "AI Note Assistant Quota Reached",
          text: "You've reached your monthly AI Note Assistant quota. Please contact your admin if you need additional access.",
          icon: "error",
          dangerMode: true,
        });
        return false;
      }
      if (pct >= 90) {
        swal({
          title: "AI Note Assistant Quota Exceeded",
          text: "You've used 90% of your AI Note Assistant quota for this month. Continued use may require admin approval.",
          icon: "warning",
          dangerMode: true,
        });
        // 90% warning — show alert but still allow AI usage (same as desktop)
      }
    }
    return true;
  };

  // ── AI generation — exact same as desktop handleAiAssistantSubmit ──────────
  const handleAiGenerate = async (fieldKey, promptType = "default") => {
    const currentText = getFieldCurrentValue(fieldKey).trim();
    if (!currentText) {
      showError("Please write something in the field first.");
      return;
    }
    if (!checkCredits()) return;

    // Show loading state in this field's preview
    setAiPreviews((prev) => ({
      ...prev,
      [fieldKey]: { loading: true, text: "", showButtons: false },
    }));
    try {
      const aiRequest = buildAiContext(fieldKey, promptType);
      const profileId = profile?.user?.admin || profile?.user?.id;
      const result = await generateAiNote(aiRequest, profileId);
      const generated =
        result?.aiReply ||
        result?.response ||
        result?.message ||
        result?.text ||
        "";
      setAiPreviews((prev) => ({
        ...prev,
        [fieldKey]: { loading: false, text: generated, showButtons: false },
      }));
    } catch (err) {
      setAiPreviews((prev) => {
        const n = { ...prev };
        delete n[fieldKey];
        return n;
      });
      // A spent token balance (403 "Token quota exceeded") gets its own message
      // with the right next step — it isn't a generation failure.
      if (!showSkaiTokenError(err)) {
        showError(err?.response?.data?.message || "AI generation failed");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  };

  // ── Draft auto-save ────────────────────────────────────────────────────────
  const stateRef = useRef({});
  const lastSavedRef = useRef("");
  stateRef.current = {
    noteTypeObj,
    formValues,
    selectedFacility,
    descriptions,
    essentialSupport,
    goals,
    goalItems,
    supportPlan,
    essentialSupportToggle,
    interventionPlanToggle,
    outComeTitle,
    outcomeNumbers,
    essentialSupportCount,
    essentialSupportTypeState,
    frequencyOptions,
    activities,
    essentialNumbers,
    goalOptions,
    objectivesByGoal,
    interventionsByGoal,
  };

  useEffect(() => {
    const id = setInterval(async () => {
      const s = stateRef.current;
      if (!s.noteTypeObj || !clientId || !clientFacilityId) return;
      const snap = JSON.stringify({
        fields: s.formValues,
        selectedFacility: s.selectedFacility,
        descriptions: s.descriptions,
        essentialSupport: s.essentialSupport,
        goals: s.goals,
        goalItems: s.goalItems,
        supportPlan: s.supportPlan,
        essentialSupportToggle: s.essentialSupportToggle,
        interventionPlanToggle: s.interventionPlanToggle,
        outComeTitle: s.outComeTitle,
        outcomeNumbers: s.outcomeNumbers,
        essentialSupportCount: s.essentialSupportCount,
        essentialSupportTypeState: s.essentialSupportTypeState,
        frequencyOptions: s.frequencyOptions,
        activities: s.activities,
        essentialNumbers: s.essentialNumbers,
        goalOptions: s.goalOptions,
        objectivesByGoal: s.objectivesByGoal,
        interventionsByGoal: s.interventionsByGoal,
      });
      if (snap === lastSavedRef.current) return;
      setDraftStatus("saving");
      const ok = await saveNoteDraft({
        noteTypeId: s.noteTypeObj.id || s.noteTypeObj._id,
        clientId,
        facilityId: clientFacilityId,
        formState: JSON.parse(snap),
      });
      if (ok) {
        lastSavedRef.current = snap;
        setDraftStatus("saved");
        setTimeout(() => setDraftStatus("idle"), 2500);
      } else {
        setDraftStatus("idle");
      }
    }, 3000);
    return () => clearInterval(id);
  }, []);

  // ── Essential support validation (mirrors desktop validateEssentialSupport) ──
  const validateEssentialSupportEntries = () => {
    const errors = essentialSupport.map(
      (item) => !item.essentialActivityNumber || !item.essentialSupportType,
    );
    setEssentialSupportErrors(errors);
    return !errors.includes(true);
  };

  // ── Validation ─────────────────────────────────────────────────────────────
  const validateForm = () => {
    if (supportPlan === "yes") {
      const newErrors = {};
      descriptions.forEach((d, i) => {
        if (!d.outcomeTitle || !d.outcomeNumber)
          newErrors[i] = "Outcome selection required";
      });
      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        return false;
      }
    }
    if (isEssentialNote && essentialSupportToggle === "yes") {
      // Per-entry validation with inline error highlighting (desktop behaviour)
      if (!validateEssentialSupportEntries()) {
        return false;
      }
    }
    if (isGroupNote && !selectedGroupClients?.length) {
      swal({
        title: "Client(s) Involved is required",
        text: "Please select at least one client for this group note.",
        icon: "warning",
        dangerMode: true,
      });
      return false;
    }
    if (isIncidentReport) {
      if (!formValues?.["Description"]?.trim()) {
        swal({
          title: "Description of Incident is required",
          text: "Please describe what happened.",
          icon: "warning",
          dangerMode: true,
        });
        return false;
      }
      if (!formValues?.["Immediate Actions Taken"]?.trim()) {
        swal({
          title: "Immediate Actions Taken is required",
          text: "Please describe the immediate actions taken.",
          icon: "warning",
          dangerMode: true,
        });
        return false;
      }
      if (!incidentNoteData.type) {
        swal({
          title: "Incident Type is required",
          text: "Please select the incident type.",
          icon: "warning",
          dangerMode: true,
        });
        return false;
      }
      if (
        incidentNoteData.type === "Other" &&
        !incidentNoteData.otherType?.trim()
      ) {
        swal({
          title: "Specify Incident Type is required",
          text: "Please describe the incident type.",
          icon: "warning",
          dangerMode: true,
        });
        return false;
      }
      if (!incidentNoteData.severity) {
        swal({
          title: "Severity is required",
          text: "Please select the severity of this incident.",
          icon: "warning",
          dangerMode: true,
        });
        return false;
      }
      if (!incidentNoteData.clientsInvolved?.length) {
        swal({
          title: "Client(s) Involved is required",
          text: "Please select at least one client involved in this incident.",
          icon: "warning",
          dangerMode: true,
        });
        return false;
      }
      if (!incidentNoteData.staffInvolved?.length) {
        swal({
          title: "Staff & Witnesses Present is required",
          text: "Please select at least one staff member for this incident.",
          icon: "warning",
          dangerMode: true,
        });
        return false;
      }
      if (
        incidentNoteData.injurySustained &&
        typeof incidentNoteData.injurySustained === "object" &&
        !incidentNoteData.injurySustained.medicalAttention
      ) {
        swal({
          title: "Medical Attention is required",
          text: "Please select the level of medical attention provided.",
          icon: "warning",
          dangerMode: true,
        });
        return false;
      }
      if (
        incidentNoteData.emergencyServicesCalled &&
        typeof incidentNoteData.emergencyServicesCalled === "object" &&
        incidentNoteData.emergencyServicesCalled.clientTransported &&
        !incidentNoteData.emergencyServicesCalled.receivingHospital?.trim()
      ) {
        swal({
          title: "Receiving Hospital / Facility is required",
          text: "Please enter the receiving hospital or facility.",
          icon: "warning",
          dangerMode: true,
        });
        return false;
      }
    }
    return true;
  };

  // ── Submit ─────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!noteTypeObj) return;
    if (!validateForm()) return;
    const NOTETYPE = noteTypeObj.id || noteTypeObj._id;

    // Convert date-type fields from "YYYY-MM-DD" strings → Date objects so MongoDB
    // stores them as ISODate (matching desktop). NoteModalNew's heuristic only
    // converts back to Date when it sees full ISO strings ("YYYY-MM-DDTHH:mm:ss.sssZ").
    const processedFormValues = { ...formValues };
    (noteTypeObj?.custom || []).forEach((f) => {
      const typeName = f?.fieldType?.name;
      if ((typeName === "date" || typeName === "Date") && processedFormValues[f.field]) {
        const val = processedFormValues[f.field];
        if (typeof val === "string") {
          processedFormValues[f.field] = new Date(val);
        }
      }
    });

    const obj = {
      noteId: NOTETYPE,
      fields: { ...processedFormValues, noteId: NOTETYPE },
      supportPlan,
      userId: clientId,
    };
    if (isGroupNote) {
      obj.fields.clientIds = selectedGroupClients;
    }
    if (isIncidentReport) {
      // Incident Report stores multiple clients/staff under the SAME field
      // names every other note type uses ("Client Name" / "Staff Name") — the
      // value is an array here instead of a single id (mirrors desktop).
      obj.fields["Client Name"] = incidentNoteData.clientsInvolved;
      obj.fields["Staff Name"] = incidentNoteData.staffInvolved;
      obj.incidentDetails = {
        incidentType: incidentNoteData.type,
        otherType:
          incidentNoteData.type === "Other" ? incidentNoteData.otherType : "",
        severity: incidentNoteData.severity,
        injurySustained: incidentNoteData.injurySustained,
        emergencyServicesCalled: incidentNoteData.emergencyServicesCalled,
        restraintUsed: incidentNoteData.restraintUsed,
        notifiedContacts: incidentNoteData.notifiedContacts,
        notificationNotes: incidentNoteData.notificationNotes,
        followUpAction: incidentNoteData.followUpAction,
      };
    }
    if (
      isProgressNote ||
      noteTypeObj?.custom?.find((c) => c?.field === "Support Plan")
    ) {
      obj.descriptions = descriptions;
    }
    if (isEssentialNote) {
      obj.essentialSupport = essentialSupport;
      obj.essentialSupportToggle = essentialSupportToggle;
    }
    if (isInterventionNote) {
      obj.goals = goals.map((g, idx) => ({
        ...g,
        goalActivities: goalItems[idx] || g.goalActivities,
      }));
      obj.interventionPlanToggle = interventionPlanToggle;
    }
    setFormLoading(true);
    try {
      await createNote(obj, () => {
        resetNoteForm();
      });
      resetNoteForm();
      // Show pending approval modal (same as desktop NoteDrawer)
      setPendingModal(true);
    } catch (err) {
      showError(err?.response?.data?.message || "Failed to save note");
    } finally {
      setFormLoading(false);
    }
  };

  const resetNoteForm = () => {
    setNoteTypeObj(null);
    setFormValues({});
    setSelectedGroupClients([]);
    setIncidentNoteData(initialIncidentNoteData);
    setSupportPlan("no");
    setServiceNumber(null);
    setDescriptions([mkInitialDesc()]);
    setOpenedAccordion("0");
    setEssentialSupportToggle("no");
    setEssentialSupport([mkInitialES()]);
    setEssentialAccordion("0");
    setFrequencyOptions([[]]);
    setActivities([[]]);
    setInterventionPlanToggle("no");
    setGoals([mkInitialGoal()]);
    setGoalsAccordion("0");
    setGoalItems([
      [{ id: Date.now(), objectives: "", Intervention_Instruction: "" }],
    ]);
    setErrors({});
    setEssentialNumbers([[]]);
    if (isListening) {
      stopListening();
      clearInterval(timerRef.current);
      setRecordingTime(0);
      setIsListening(false);
      setListeningFor(null);
    }
    setAiPreviews({});
    lastSavedRef.current = "";
  };

  // ── Format recording timer ─────────────────────────────────────────────────
  const fmtTime = (s) => {
    const m = Math.floor(s / 60)
      .toString()
      .padStart(2, "0");
    const sec = (s % 60).toString().padStart(2, "0");
    return `${m}:${sec}`;
  };

  // ── Textarea with voice + AI — exact desktop behaviour ─────────────────────
  // Plain render function (not a component) so React never unmounts/remounts it on re-render
  const renderTextareaField = ({
    label,
    value,
    onChange,
    fieldKey,
    rows = 5,
    placeholder,
    goalPromptType,
  }) => {
    const pv = aiPreviews[fieldKey]; // { loading, text, showButtons } | undefined
    const creditsDisabled =
      existingRecord?.active === false && existingRecord?.paid === false;
    const isRecordingThis = isListening && listeningFor === fieldKey;
    const hasText = value.trim().length > 0;

    const closePreview = () =>
      setAiPreviews((prev) => {
        const n = { ...prev };
        delete n[fieldKey];
        return n;
      });

    return (
      <div className="mc-form-field">
        {label && <label className="mc-form-label">{label}</label>}
        <div className="mc-textarea-block">
          {/* Main textarea */}
          <textarea
            className="mc-form-textarea mc-form-textarea--attached"
            placeholder={placeholder || `Enter ${label || "text"}…`}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            rows={rows}
          />

          {/* ── Bottom bar (always shown) ── */}
          {!pv && (
            <div className="mc-ai-inline-bar">
              {/* Left: Credit Balance */}
              {isAiAccess && creditLabel !== null && (
                <span className="mc-ai-inline-credit">
                  Credit Balance:{" "}
                  <strong>
                    {creditLabel === "Unlimited" ? "Unlimited" : creditLabel}
                  </strong>
                </span>
              )}
              {/* Right: Mic + AutoFill */}
              <div className="mc-ai-inline-actions">
                <button
                  type="button"
                  className={`mc-ai-inline-btn${isRecordingThis ? " mc-ai-inline-btn--mic-active" : ""}`}
                  onClick={() => toggleListening(fieldKey)}
                  disabled={creditsDisabled}
                  title="Voice dictation"
                >
                  <MicIcon />
                </button>
                {isAiAccess && (
                  <button
                    type="button"
                    className="mc-ai-inline-btn mc-ai-inline-btn--ai"
                    disabled={creditsDisabled || !hasText}
                    style={{
                      opacity: hasText ? 1 : 0.4,
                      cursor: hasText ? "pointer" : "not-allowed",
                    }}
                    onClick={() =>
                      setAiPreviews((prev) => ({
                        ...prev,
                        [fieldKey]: {
                          loading: false,
                          text: "",
                          showButtons: true,
                        },
                      }))
                    }
                    title="AI Note Assistant"
                  >
                    <AiStarIcon />
                  </button>
                )}
              </div>
            </div>
          )}

          {/* ── Recording controls (waveform + timer + Redo/Cancel/✓) ── */}
          {isRecordingThis && (
            <div className="mc-recording-bar">
              <div className="mc-recording-wave">
                {[...Array(12)].map((_, i) => (
                  <span
                    key={i}
                    className="mc-wave-bar"
                    style={{ animationDelay: `${i * 0.08}s` }}
                  />
                ))}
              </div>
              <div className="mc-recording-controls">
                <button
                  type="button"
                  className="mc-rec-btn"
                  onClick={() => handleMicRedo(fieldKey)}
                  title="Redo"
                >
                  ↺ Redo
                </button>
                <span className="mc-rec-timer">{fmtTime(recordingTime)}</span>
                <button
                  type="button"
                  className="mc-rec-btn mc-rec-btn--cancel"
                  onClick={() => handleMicCancel(fieldKey)}
                  title="Cancel"
                >
                  ✕
                </button>
                <button
                  type="button"
                  className="mc-rec-btn mc-rec-btn--confirm"
                  onClick={() => toggleListening(fieldKey)}
                  title="Confirm"
                >
                  ✓
                </button>
              </div>
            </div>
          )}

          {/* ── AI assistant panel ── */}
          {isAiAccess && pv && (
            <div className="mc-ai-preview-panel">
              {/* Header */}
              <div className="mc-ai-preview-header">
                <AiStarIcon />
                <span>AI Assistant</span>
                <button
                  type="button"
                  className="mc-ai-preview-close"
                  onClick={closePreview}
                >
                  ✕
                </button>
              </div>

              {/* AI action buttons — goal fields get dedicated Behavior/Response/Plan button */}
              {pv.showButtons && !pv.loading && !pv.text && (
                <div className="mc-ai-action-btns">
                  {goalPromptType ? (
                    // Dedicated button for Behavior / Response / Plan fields
                    <>
                      <button
                        type="button"
                        className="mc-ai-action-btn"
                        onClick={() =>
                          handleAiGenerate(fieldKey, goalPromptType)
                        }
                      >
                        Generate{" "}
                        {goalPromptType.charAt(0).toUpperCase() +
                          goalPromptType.slice(1)}
                      </button>
                    </>
                  ) : (
                    // Standard Elaborate / Rewrite / Polish for regular description fields
                    [
                      { label: "Elaborate", type: "elaborate" },
                      { label: "Rewrite", type: "rewrite" },
                      { label: "Polish", type: "polish" },
                    ].map((btn) => (
                      <button
                        key={btn.type}
                        type="button"
                        className="mc-ai-action-btn"
                        onClick={() => handleAiGenerate(fieldKey, btn.type)}
                      >
                        {btn.label}
                      </button>
                    ))
                  )}
                </div>
              )}

              {/* Loading */}
              {pv.loading && (
                <div className="mc-ai-preview-loading">
                  <span className="mc-ai-spinner" />
                  <span>Generating…</span>
                </div>
              )}

              {/* Generated text preview */}
              {!pv.loading && pv.text && (
                <>
                  <p className="mc-ai-preview-text">{pv.text}</p>
                  <div className="mc-ai-preview-actions">
                    <button
                      type="button"
                      className="mc-ai-preview-btn mc-ai-preview-btn--secondary"
                      onClick={() =>
                        handleAiGenerate(fieldKey, goalPromptType || "default")
                      }
                    >
                      Regenerate
                    </button>
                    <button
                      type="button"
                      className="mc-ai-preview-btn mc-ai-preview-btn--primary"
                      onClick={() => {
                        applyAiPreview(fieldKey, pv.text);
                        closePreview();
                      }}
                    >
                      Replace Text
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  // ── Group Note — Client Name. This form is always opened from one client's
  // chart, so — mirroring desktop's userId-locked behaviour (same treatment
  // as Incident Report's Client(s) Involved field) — this is pre-filled with
  // that one client and locked, not a free multi-client picker.
  const renderGroupClientsField = () => {
    const groupClientOptions = (selectedGroupClients || []).map((id) => {
      const p = allPatients?.find((pp) => pp?.id === id || pp?._id === id);
      return p ? { value: id, label: p.name } : { value: id, label: id };
    });
    return (
      <div className="mc-form-field">
        <MultiSelect
          label="Client Name"
          placeholder="Select clients…"
          data={groupClientOptions}
          value={selectedGroupClients}
          onChange={() => {}}
          disabled
          classNames={{
            input: "mc-mantine-select-input",
            dropdown: "mc-mantine-dropdown",
            value: "mc-mantine-ms-pill",
          }}
        />
      </div>
    );
  };

  // ── Incident Report — Client(s) Involved. This form is always opened from
  // one client's chart, so — mirroring desktop's userId-locked behaviour —
  // the field is pre-filled with that client and locked, not freely editable.
  const renderIncidentClientsField = () => {
    const involvedOptions = (incidentNoteData.clientsInvolved || [])
      .map((id) => {
        const p = allPatients?.find((pp) => pp?.id === id || pp?._id === id);
        return p ? { value: id, label: p.name } : { value: id, label: id };
      });
    return (
      <div className="mc-form-field">
        <MultiSelect
          label="Client(s) Involved *"
          placeholder="Select client(s)…"
          data={involvedOptions}
          value={incidentNoteData.clientsInvolved || []}
          onChange={() => {}}
          disabled
          classNames={{
            input: "mc-mantine-select-input",
            dropdown: "mc-mantine-dropdown",
            value: "mc-mantine-ms-pill",
          }}
        />
      </div>
    );
  };

  // ── Incident Report — Staff & Witnesses Present (mirrors desktop MultiSelect
  // and "Select All"). An incident usually involves more than the person filing
  // it, so staff can add the other staff and witnesses who were present. ──────
  const renderIncidentStaffField = () => {
    const staffOptions =
      staffStore?.staffs
        ?.filter((s) => s?.active === true && !s?.deleted && s?.type !== "contractor")
        ?.map((s) => ({ value: s.id || s._id, label: s.name })) || [];
    return (
      <div className="mc-form-field">
        <MultiSelect
          label="Staff & Witnesses Present *"
          placeholder="Select staff & witnesses…"
          data={[{ value: "ALL", label: "Select All" }, ...staffOptions]}
          value={incidentNoteData.staffInvolved || []}
          onChange={(val) => {
            if (val.includes("ALL")) {
              setIncidentNoteData((prev) => ({
                ...prev,
                staffInvolved: staffOptions.map((o) => o.value),
              }));
            } else {
              setIncidentNoteData((prev) => ({ ...prev, staffInvolved: val }));
            }
          }}
          searchable
          rightSection={<LuChevronDown size={14} color="#6B7280" />}
          rightSectionPointerEvents="none"
          classNames={{
            input: "mc-mantine-select-input",
            dropdown: "mc-mantine-dropdown",
            value: "mc-mantine-ms-pill",
            item: "mc-mantine-ms-item",
          }}
          maxDropdownHeight={240}
        />
      </div>
    );
  };

  // ── Incident Report — shared Yes/No toggle (mirrors desktop incidentYesNoGroup) ──
  const renderYesNoToggle = (checked, onYes, onNo) => (
    <div className="mc-incident-yesno-group">
      {["No", "Yes"].map((option) => (
        <div
          key={option}
          className={`mc-incident-yesno-option ${
            (option === "Yes") === !!checked
              ? "mc-incident-yesno-option--active"
              : ""
          }`}
          onClick={() => (option === "Yes" ? onYes() : onNo())}
        >
          {option}
        </div>
      ))}
    </div>
  );

  // ── Render a single custom field ───────────────────────────────────────────
  const renderField = (fieldDef) => {
    const { field: name, fieldType, placeholder, required } = fieldDef;
    const typeName = fieldType?.name;
    const value = formValues[name] ?? "";
    const onChange = (val) => setFV(name, val);

    if (isClientField(fieldDef)) return null;

    // Description textarea — only "Description" field gets mic + AI (matches desktop)
    if (name === "Description") {
      if (isProgressNote && supportPlan === "yes") return null;
      if (isEssentialNote && essentialSupportToggle === "yes") return null;
      if (isInterventionNote && interventionPlanToggle === "yes") return null;
      return renderTextareaField({
        label: `${name}${required ? " *" : ""}`,
        value,
        onChange,
        fieldKey: `main-${name}`,
        rows: 6,
        placeholder: placeholder || `Enter ${name}…`,
      });
    }

    // Other text area type fields — plain textarea, no mic/AI (matches desktop)
    if (typeName === "text area") {
      return (
        <div key={name} className="mc-form-field">
          <label className="mc-form-label">
            {name}
            {required ? " *" : ""}
          </label>
          <textarea
            className="mc-form-textarea"
            placeholder={placeholder || `Enter ${name}…`}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            rows={5}
          />
        </div>
      );
    }

    // Staff select
    if (isStaffField(fieldDef)) {
      const activeStaff =
        staffStore?.staffs
          ?.filter((s) => s.active === true && !s.deleted)
          ?.map((s) => ({ value: s.id || s._id, label: s.name })) || [];
      return (
        <div key={name} className="mc-form-field">
          <Select
            label={name}
            placeholder="Select staff…"
            data={activeStaff}
            value={value || selectedStaff || null}
            disabled={userType === "staff"}
            onChange={(val) => {
              onChange(val || "");
              setSelectedStaff(val || "");
            }}
            searchable
            rightSection={<LuChevronDown size={14} color="#6B7280" />}
            rightSectionPointerEvents="none"
            classNames={{
              input: "mc-mantine-select-input",
              dropdown: "mc-mantine-dropdown",
            }}
          />
        </div>
      );
    }

    // Facility select
    if (isFacilityField(fieldDef)) {
      return (
        <div key={name} className="mc-form-field">
          <Select
            label={name}
            placeholder="Select facility…"
            data={allFacilities
              .filter((f) =>
                (clientDetails?.facilityId || []).some(
                  (_f) => (_f?.id || _f?._id || _f) === (f.id || f._id),
                ),
              )
              .map((f) => ({ value: f._id || f.id, label: f.name }))}
            value={value || null}
            onChange={(val) => {
              onChange(val || "");
              setSelectedFacility(val || "");
            }}
            searchable
            rightSection={<LuChevronDown size={14} color="#6B7280" />}
            rightSectionPointerEvents="none"
            classNames={{
              input: "mc-mantine-select-input",
              dropdown: "mc-mantine-dropdown",
            }}
          />
        </div>
      );
    }

    // Date
    if (typeName === "date" || typeName === "Date") {
      const dateVal = value
        ? value instanceof Date
          ? value.toISOString().slice(0, 10)
          : String(value).slice(0, 10)
        : "";
      return (
        <div key={name} className="mc-form-field">
          <label className="mc-form-label">
            {name}
            {required ? " *" : ""}
          </label>
          <input
            className="mc-form-input"
            type="date"
            value={dateVal}
            onChange={(e) => onChange(e.target.value)}
          />
        </div>
      );
    }

    // Time
    if (typeName === "time" || typeName === "Time") {
      return (
        <div key={name} className="mc-form-field">
          <label className="mc-form-label">
            {name}
            {required ? " *" : ""}
          </label>
          <input
            className="mc-form-input"
            type="time"
            value={value}
            onChange={(e) => onChange(e.target.value)}
          />
        </div>
      );
    }

    // Number input — mirrors desktop TextInput type="number"
    if (typeName === "number") {
      return (
        <div key={name} className="mc-form-field">
          <label className="mc-form-label">
            {name}
            {required ? " *" : ""}
          </label>
          <input
            className="mc-form-input"
            type="number"
            placeholder={placeholder || `Enter ${name}…`}
            value={value}
            onChange={(e) => onChange(e.target.value)}
          />
        </div>
      );
    }

    // String input — mirrors desktop TextInput type="text"
    if (typeName === "string") {
      return (
        <div key={name} className="mc-form-field">
          <label className="mc-form-label">
            {name}
            {required ? " *" : ""}
          </label>
          <input
            className="mc-form-input"
            type="text"
            placeholder={placeholder || `Enter ${name}…`}
            value={value}
            onChange={(e) => onChange(e.target.value)}
          />
        </div>
      );
    }

    // Level of Need (dropdown with fixed options — mirrors desktop "level of need" key)
    if (typeName?.toLowerCase() === "level of need") {
      const opts = getOptions("level of need", noteTypeObj?.custom) || [];
      return (
        <div key={name} className="mc-form-field">
          <Select
            label={`${name}${required ? " *" : ""}`}
            placeholder={`Select ${name}…`}
            data={opts}
            value={value || null}
            onChange={(val) => onChange(val || "")}
            searchable
            rightSection={<LuChevronDown size={14} color="#6B7280" />}
            rightSectionPointerEvents="none"
            classNames={{
              input: "mc-mantine-select-input",
              dropdown: "mc-mantine-dropdown",
            }}
          />
        </div>
      );
    }

    // Other (comma-separated dropdown) — uses getOptions like desktop
    if (typeName === "Other") {
      const opts = getOptions("other", noteTypeObj?.custom, name) || [];
      return (
        <div key={name} className="mc-form-field">
          <Select
            label={`${name}${required ? " *" : ""}`}
            placeholder={`Select ${name}…`}
            data={opts}
            value={value || null}
            onChange={(val) => onChange(val || "")}
            searchable
            rightSection={<LuChevronDown size={14} color="#6B7280" />}
            rightSectionPointerEvents="none"
            classNames={{
              input: "mc-mantine-select-input",
              dropdown: "mc-mantine-dropdown",
            }}
          />
        </div>
      );
    }

    // Dropdown — uses getOptions like desktop
    if (typeName === "Dropdown") {
      const opts = getOptions("dropdown", noteTypeObj?.custom, name) || [];
      return (
        <div key={name} className="mc-form-field">
          <Select
            label={`${name}${required ? " *" : ""}`}
            placeholder={`Select ${name}…`}
            data={opts}
            value={value || null}
            onChange={(val) => onChange(val || "")}
            searchable
            rightSection={<LuChevronDown size={14} color="#6B7280" />}
            rightSectionPointerEvents="none"
            classNames={{
              input: "mc-mantine-select-input",
              dropdown: "mc-mantine-dropdown",
            }}
          />
        </div>
      );
    }

    // Multiple Choice (checkboxes) — uses getOptions like desktop
    if (typeName === "Multiple Choice") {
      const opts =
        getOptions("multiple choices", noteTypeObj?.custom, name) || [];
      const selected = Array.isArray(value) ? value : value ? [value] : [];
      return (
        <div key={name} className="mc-form-field">
          <label className="mc-form-label">
            {name}
            {required ? " *" : ""}
          </label>
          <div className="mc-checkbox-group">
            {opts.map((o) => (
              <label key={o.value} className="mc-checkbox-item">
                <input
                  type="checkbox"
                  checked={selected.includes(o.value)}
                  onChange={(e) =>
                    onChange(
                      e.target.checked
                        ? [...selected, o.value]
                        : selected.filter((v) => v !== o.value),
                    )
                  }
                />
                <span>{o.label}</span>
              </label>
            ))}
          </div>
        </div>
      );
    }

    // Single Choice (radios) — uses getOptions like desktop
    if (typeName === "Single Choice") {
      const opts = getOptions("single choice", noteTypeObj?.custom, name) || [];
      return (
        <div key={name} className="mc-form-field">
          <label className="mc-form-label">
            {name}
            {required ? " *" : ""}
          </label>
          <div className="mc-radio-group">
            {opts.map((o) => (
              <label key={o.value} className="mc-radio-item">
                <input
                  type="radio"
                  name={`nr-${name}`}
                  value={o.value}
                  checked={value === o.value}
                  onChange={() => onChange(o.value)}
                />
                <span>{o.label}</span>
              </label>
            ))}
          </div>
        </div>
      );
    }

    // Support Plan field — handled as radio toggle only, never via renderField (matches desktop)
    if (name === "Support Plan") return null;

    // Medication Records
    if (typeName === "Medication Records") {
      return (
        <div key={name} className="mc-form-field">
          <Select
            label={name}
            placeholder="Select medication…"
            data={medicationOptions}
            value={value || null}
            onChange={(val) => onChange(val || "")}
            searchable
            rightSection={<LuChevronDown size={14} color="#6B7280" />}
            rightSectionPointerEvents="none"
            classNames={{
              input: "mc-mantine-select-input",
              dropdown: "mc-mantine-dropdown",
            }}
          />
          {value && (
            <textarea
              className="mc-form-textarea"
              value={value}
              readOnly
              rows={4}
              style={{ opacity: 0.75, cursor: "default", marginTop: 6 }}
            />
          )}
        </div>
      );
    }

    // Facility Types
    if (typeName === "Facility Types") {
      return (
        <div key={name} className="mc-form-field">
          <label className="mc-form-label">{name}</label>
          <input
            className="mc-form-input"
            type="text"
            placeholder={`Enter ${name}…`}
            value={value}
            onChange={(e) => onChange(e.target.value)}
          />
        </div>
      );
    }

    // Signature field — render the staff signature block
    if (typeName === "Signature") {
      return renderSignature();
    }

    // Unknown field type — return null (matches desktop behaviour)
    return null;
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="mc-add-form">
      {/* App bar — matches HTML design: back arrow + "New note" title */}
      <div className="mc-form-app-bar">
        <button
          className="mc-icon-btn"
          type="button"
          aria-label="Back"
          onClick={() => onTabSwitch("facesheet")}
        >
          <LuChevronLeft size={18} strokeWidth={2.5} />
        </button>
        <div className="mc-form-app-bar-title">New note</div>
        <div className="mc-icon-btn" style={{ visibility: "hidden" }} />
      </div>

      {/* Client bar */}
      <ClientBar clientName={clientName} clientDetails={clientDetails} />

      {/* Draft status */}
      {draftStatus && (
        <div className="mc-draft-status-bar">
          {draftStatus === "saving" && (
            <span className="mc-draft-status">Saving draft…</span>
          )}
          {draftStatus === "saved" && (
            <span className="mc-draft-status mc-draft-status--saved">
              Draft saved
            </span>
          )}
          {draftStatus === "restored" && (
            <span className="mc-draft-status mc-draft-status--restored">
              Draft restored
            </span>
          )}
        </div>
      )}

      {/* Draft restore banner */}
      {pendingDraft && (
        <div className="mc-draft-banner">
          <span className="mc-draft-banner-text">
            📝 You have an unsaved draft. Continue editing?
          </span>
          <div className="mc-draft-banner-actions">
            <button
              type="button"
              className="mc-draft-banner-btn mc-draft-banner-btn--restore"
              onClick={restoreDraft}
            >
              Continue editing
            </button>
            <button
              type="button"
              className="mc-draft-banner-btn mc-draft-banner-btn--dismiss"
              onClick={() => setPendingDraft(null)}
            >
              Discard
            </button>
          </div>
        </div>
      )}

      {/* Scrollable body */}
      <div className="mc-form-body">
        {/* Note type selector */}
        <div className="mc-form-field">
          <Select
            label="Note type"
            placeholder="Select note type…"
            data={noteTypes.map((t) => ({
              value: t._id || t.id,
              label: t.name,
            }))}
            value={noteTypeObj ? noteTypeObj._id || noteTypeObj.id : null}
            onChange={(val) => handleNoteTypeChange(val || "")}
            searchable
            rightSection={<LuChevronDown size={14} color="#6B7280" />}
            rightSectionPointerEvents="none"
            classNames={{
              input: "mc-mantine-select-input",
              dropdown: "mc-mantine-dropdown",
            }}
          />
        </div>

        {noteTypeObj && (
          <>
            {/* ── 1. Pinned fields (desktop order: Level of Need → Staff → Client → Facility → Date → Time → Shift) ── */}
            {(noteTypeObj.custom || [])
              .filter((f) => f.field === "Level of Need")
              .map((f, i) => (
                <span key={`lon-${i}`}>{renderField(f)}</span>
              ))}
            {(noteTypeObj.custom || [])
              .filter((f) => f.field === "Staff Name")
              .map((f, i) => (
                <span key={`sn-${i}`}>
                  {isIncidentReport ? renderIncidentStaffField() : renderField(f)}
                </span>
              ))}
            {(noteTypeObj.custom || [])
              .filter((f) => f.field === "Client Name")
              .map((f, i) => (
                <span key={`cn-${i}`}>
                  {isIncidentReport
                    ? renderIncidentClientsField()
                    : isGroupNote
                      ? renderGroupClientsField()
                      : renderField(f)}
                </span>
              ))}
            {!isBillingServices &&
              (noteTypeObj.custom || [])
                .filter((f) => f.field === "Facility Name")
                .map((f, i) => <span key={`fn-${i}`}>{renderField(f)}</span>)}
            {(noteTypeObj.custom || [])
              .filter((f) => f.fieldType?.name?.toLowerCase() === "date")
              .map((f, i) => (
                <span key={`date-${i}`}>{renderField(f)}</span>
              ))}
            {(noteTypeObj.custom || [])
              .filter((f) => f.fieldType?.name === "time")
              .map((f, i) => (
                <span key={`time-${i}`}>{renderField(f)}</span>
              ))}
            {/* Location — Incident Report's "when & where" field (desktop
                renders this unconditionally by field name, not by general/
                field_type, so bypass those guards here too). */}
            {isIncidentReport &&
              (noteTypeObj.custom || [])
                .filter((f) => f.field === "Location")
                .map((f, i) => (
                  <span key={`loc-${i}`}>{renderField(f)}</span>
                ))}
            {noteTypeObj?.general === true &&
              (noteTypeObj.custom || [])
                .filter((f) => f.field === "Shift")
                .map((f, i) => (
                  <span key={`shift-${i}`}>{renderField(f)}</span>
                ))}

            {/* ── 1b. Incident Report — Incident Classification (mirrors desktop) ── */}
            {isIncidentReport && (
              <div className="mc-note-card mc-incident-card">
                <div className="mc-form-group-title-row">
                  <span className="mc-form-group-icon">
                    <LuBookOpen />
                  </span>
                  <span className="mc-form-group-title">
                    INCIDENT CLASSIFICATION
                  </span>
                </div>

                <div className="mc-form-field">
                  <Select
                    label="Incident Type *"
                    placeholder="Select Incident Type"
                    data={[
                      "Fall",
                      "Medication Error",
                      "Behavioral / Aggression",
                      "Elopement / Wandering",
                      "Self-Injurious Behavior",
                      "Injury (Accidental)",
                      "Medical Emergency",
                      "Property Damage",
                      "Allegation of Abuse / Neglect",
                      "Other",
                    ]}
                    value={incidentNoteData.type}
                    onChange={(val) =>
                      setIncidentNoteData((prev) => ({ ...prev, type: val }))
                    }
                    searchable
                    rightSection={<LuChevronDown size={14} color="#6B7280" />}
                    rightSectionPointerEvents="none"
                    classNames={{
                      input: "mc-mantine-select-input",
                      dropdown: "mc-mantine-dropdown",
                    }}
                  />
                </div>

                {incidentNoteData.type === "Other" && (
                  <div className="mc-form-field">
                    <label className="mc-form-label">
                      Specify Incident Type *
                    </label>
                    <input
                      className="mc-form-input"
                      type="text"
                      placeholder="Describe the type of incident"
                      value={incidentNoteData.otherType || ""}
                      onChange={(e) =>
                        setIncidentNoteData((prev) => ({
                          ...prev,
                          otherType: e.target.value,
                        }))
                      }
                    />
                  </div>
                )}

                <div className="mc-form-field">
                  <label className="mc-form-label">Severity *</label>
                  <div className="mc-incident-severity-group">
                    {["Minor", "Moderate", "Serious", "Critical"].map(
                      (option) => (
                        <div
                          key={option}
                          className={`mc-incident-severity-option ${
                            incidentNoteData.severity === option
                              ? "mc-incident-severity-option--active"
                              : ""
                          }`}
                          style={
                            incidentNoteData.severity === option
                              ? {
                                  borderColor: severityColorMap[option].border,
                                  color: severityColorMap[option].text,
                                  backgroundColor:
                                    severityColorMap[option].background,
                                }
                              : undefined
                          }
                          onClick={() =>
                            setIncidentNoteData((prev) => ({
                              ...prev,
                              severity: option,
                            }))
                          }
                        >
                          <span
                            className="mc-incident-severity-dot"
                            style={{
                              backgroundColor: severityColorMap[option].dot,
                            }}
                          />
                          {option}
                        </div>
                      ),
                    )}
                  </div>
                </div>

                {(incidentNoteData.severity === "Serious" ||
                  incidentNoteData.severity === "Critical") && (
                  <div
                    className="mc-incident-banner"
                    style={{
                      borderColor:
                        severityColorMap[incidentNoteData.severity].border,
                      backgroundColor:
                        severityColorMap[incidentNoteData.severity]
                          .background,
                    }}
                  >
                    <LuAlertTriangle
                      size={16}
                      color={severityColorMap[incidentNoteData.severity].text}
                    />
                    <div>
                      <div
                        className="mc-incident-banner-title"
                        style={{
                          color:
                            severityColorMap[incidentNoteData.severity].text,
                        }}
                      >
                        {incidentNoteData.severity === "Critical"
                          ? "Critical incident — immediate reporting required"
                          : "Reportable incident"}
                      </div>
                      <div
                        className="mc-incident-banner-text"
                        style={{
                          color:
                            severityColorMap[incidentNoteData.severity].text,
                        }}
                      >
                        {incidentNoteData.severity === "Critical"
                          ? "Notify the agency on-call supervisor immediately and file the state reportable-incident report within 24 hours. Agency notification and medical review are required below."
                          : "This severity level is reportable. Agency notification is required and the incident must be reviewed by a supervisor within 24 hours."}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ── 2. Toggles (desktop order: Essential → Intervention → Support Plan) ── */}
            {isEssentialNote && (
              <div className="mc-form-field">
                <label className="mc-form-label">Use Essential Support</label>
                <div className="mc-radio-group mc-radio-group--inline">
                  {["yes", "no"].map((opt) => (
                    <label key={opt} className="mc-radio-item">
                      <input
                        type="radio"
                        name="essentialSupportToggle"
                        value={opt}
                        checked={essentialSupportToggle === opt}
                        onChange={() => {
                          setEssentialSupportToggle(opt);
                          if (opt === "no")
                            setFormValues((prev) => ({
                              ...prev,
                              Description: "",
                            }));
                        }}
                      />
                      <span>{opt === "yes" ? "Yes" : "No"}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {isInterventionNote && (
              <div className="mc-form-field">
                <label className="mc-form-label">Use Intervention plan</label>
                <div className="mc-radio-group mc-radio-group--inline">
                  {["yes", "no"].map((opt) => (
                    <label key={opt} className="mc-radio-item">
                      <input
                        type="radio"
                        name="interventionPlanToggle"
                        value={opt}
                        checked={interventionPlanToggle === opt}
                        onChange={() => setInterventionPlanToggle(opt)}
                      />
                      <span>{opt === "yes" ? "Yes" : "No"}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {(isProgressNote ||
              noteTypeObj?.custom?.find(
                (c) => c?.field === "Support Plan",
              )) && (
              <div className="mc-form-field">
                <label className="mc-form-label">Use Support Plan</label>
                <div className="mc-radio-group mc-radio-group--inline">
                  {["yes", "no"].map((opt) => (
                    <label key={opt} className="mc-radio-item">
                      <input
                        type="radio"
                        name="supportPlanToggle"
                        value={opt}
                        checked={supportPlan === opt}
                        onChange={() => {
                          setSupportPlan(opt);
                          if (opt === "no")
                            setFormValues((prev) => ({
                              ...prev,
                              Description: "",
                            }));
                        }}
                      />
                      <span>{opt === "yes" ? "Yes" : "No"}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* ── 3. Custom fields — mirrors desktop's per-type explicit blocks exactly ── */}

            {/* Dropdown — only when general === false (desktop guard) */}
            {noteTypeObj.general === false &&
              (noteTypeObj.custom || [])
                .filter((f) => f.fieldType?.name === "Dropdown")
                .map((f, i) => <span key={`dd-${i}`}>{renderField(f)}</span>)}

            {/* Number (Custom Field only) — only when general === false */}
            {noteTypeObj.general === false &&
              (noteTypeObj.custom || [])
                .filter(
                  (f) =>
                    f?.field_type === "Custom Field" &&
                    f.fieldType?.name === "number",
                )
                .map((f, i) => <span key={`num-${i}`}>{renderField(f)}</span>)}

            {/* Other (Custom Field only) — only when general === false */}
            {noteTypeObj.general === false &&
              (noteTypeObj.custom || [])
                .filter(
                  (f) =>
                    f?.field_type === "Custom Field" &&
                    f.fieldType?.name?.toLowerCase() === "other",
                )
                .map((f, i) => <span key={`oth-${i}`}>{renderField(f)}</span>)}

            {/* Multiple Choice — no general guard (desktop renders unconditionally) */}
            {(noteTypeObj.custom || [])
              .filter((f) => f.fieldType?.name === "Multiple Choice")
              .map((f, i) => (
                <span key={`mc-${i}`}>{renderField(f)}</span>
              ))}

            {/* Single Choice — no general guard */}
            {(noteTypeObj.custom || [])
              .filter((f) => f.fieldType?.name === "Single Choice")
              .map((f, i) => (
                <span key={`sc-${i}`}>{renderField(f)}</span>
              ))}

            {/* String (Custom Field only) — only when general === false.
                Excludes "Location", which is already rendered explicitly
                above (pinned, next to Date/Time) for Incident Report. */}
            {noteTypeObj.general === false &&
              (noteTypeObj.custom || [])
                .filter(
                  (f) =>
                    f?.field_type === "Custom Field" &&
                    f.fieldType?.name === "string" &&
                    f.field !== "Location",
                )
                .map((f, i) => <span key={`str-${i}`}>{renderField(f)}</span>)}

            {/* Medication Records — no general guard */}
            {(noteTypeObj.custom || [])
              .filter((f) => f.fieldType?.name === "Medication Records")
              .map((f, i) => (
                <span key={`mr-${i}`}>{renderField(f)}</span>
              ))}

            {/* ── 3b. Billing Services — Provider + Service (Unit Type: Days) + Unit/Day/Hours ── */}
            {isBillingServices &&
              (() => {
                // allPatients has populated providerId (objects with id+name); clientDetails fallback
                const enrichedPatient =
                  (allPatients?.patients || allPatients || []).find(
                    (p) => (p?.id || p?._id) === clientId,
                  ) || clientDetails;
                const providerList = Array.isArray(enrichedPatient?.providerId)
                  ? enrichedPatient.providerId.filter((p) => p?.id || p?._id)
                  : [];
                const providerValue =
                  providerList[0]?.id || providerList[0]?._id || "";
                const services =
                  enrichedPatient?.form1?.services ||
                  clientDetails?.form1?.services ||
                  [];
                return (
                  <>
                    {/* Provider — disabled, auto-filled from client */}
                    <div className="mc-form-field">
                      <Select
                        label="Provider"
                        data={providerList.map((p) => ({
                          value: p.id || p._id,
                          label: p.name,
                        }))}
                        value={providerValue || null}
                        disabled
                        searchable
                        rightSection={
                          <LuChevronDown size={14} color="#6B7280" />
                        }
                        rightSectionPointerEvents="none"
                        classNames={{
                          input: "mc-mantine-select-input",
                          dropdown: "mc-mantine-dropdown",
                        }}
                      />
                    </div>

                    {/* Service (Unit Type: Days) */}
                    <div className="mc-form-field">
                      <Select
                        label="Service (Unit Type: Days) *"
                        placeholder="Select Service (Unit Type: Days)"
                        data={services.map((s) => ({
                          value: s.servicenumber,
                          label: s.servicetitle,
                        }))}
                        value={formValues["Service Type"] || null}
                        onChange={(val) => {
                          setFormValues((prev) => ({
                            ...prev,
                            "Service Type": val || "",
                          }));
                          // eslint-disable-next-line eqeqeq
                          const selected = services.find(
                            (s) => s?.servicenumber == val,
                          );
                          setServiceNumber(selected || null);
                        }}
                        searchable
                        rightSection={
                          <LuChevronDown size={14} color="#6B7280" />
                        }
                        rightSectionPointerEvents="none"
                        classNames={{
                          input: "mc-mantine-select-input",
                          dropdown: "mc-mantine-dropdown",
                        }}
                      />
                      {serviceNumber && (
                        <span
                          style={{
                            fontSize: 12,
                            fontStyle: "italic",
                            color: "#aaa",
                          }}
                        >
                          Approved Units: {serviceNumber.servicenumber}
                        </span>
                      )}
                    </div>

                    {/* Unit/Day/Hours */}
                    {(noteTypeObj.custom || [])
                      .filter((f) => f.field === "Unit/Day/Hours")
                      .map((f, i) => (
                        <span key={`udh-${i}`}>{renderField(f)}</span>
                      ))}
                  </>
                );
              })()}

            {/* ── 4. Description / textarea fields ── */}
            {(noteTypeObj.custom || [])
              .filter(
                (f) =>
                  f.fieldType?.name === "text area" ||
                  f.field === "Description",
              )
              .map((f, i) => (
                <span key={`ta-${i}`}>{renderField(f)}</span>
              ))}

            {/* ── 4b. Incident Report — Injuries & Medical Attention + EMS ── */}
            {isIncidentReport && (
              <div className="mc-note-card mc-incident-card">
                <div className="mc-form-group-title-row">
                  <span className="mc-form-group-icon">
                    <LuPlus />
                  </span>
                  <span className="mc-form-group-title">
                    INJURIES &amp; MEDICAL ATTENTION
                  </span>
                </div>

                <div className="mc-form-field">
                  <label className="mc-form-label">
                    Was an injury sustained? *
                  </label>
                  {renderYesNoToggle(
                    incidentNoteData.injurySustained,
                    () =>
                      setIncidentNoteData((prev) => ({
                        ...prev,
                        injurySustained:
                          prev.injurySustained &&
                          typeof prev.injurySustained === "object"
                            ? prev.injurySustained
                            : {
                                description: "",
                                bodyLocation: "",
                                medicalAttention: "",
                              },
                      })),
                    () =>
                      setIncidentNoteData((prev) => ({
                        ...prev,
                        injurySustained: false,
                      })),
                  )}
                </div>

                {incidentNoteData.injurySustained && (
                  <>
                    <div className="mc-form-field">
                      <label className="mc-form-label">
                        Description of Injury
                      </label>
                      <input
                        className="mc-form-input"
                        type="text"
                        placeholder="e.g. superficial abrasion, minor swelling, laceration"
                        value={incidentNoteData.injurySustained.description || ""}
                        onChange={(e) =>
                          setIncidentNoteData((prev) => ({
                            ...prev,
                            injurySustained: {
                              ...prev.injurySustained,
                              description: e.target.value,
                            },
                          }))
                        }
                      />
                    </div>
                    <div className="mc-form-row-2">
                      <div className="mc-form-field">
                        <label className="mc-form-label">Body Location</label>
                        <input
                          className="mc-form-input"
                          type="text"
                          placeholder="e.g. left forearm"
                          value={
                            incidentNoteData.injurySustained.bodyLocation || ""
                          }
                          onChange={(e) =>
                            setIncidentNoteData((prev) => ({
                              ...prev,
                              injurySustained: {
                                ...prev.injurySustained,
                                bodyLocation: e.target.value,
                              },
                            }))
                          }
                        />
                      </div>
                      <div className="mc-form-field">
                        <Select
                          label="Medical Attention *"
                          placeholder="Select"
                          data={[
                            "None required",
                            "First aid on site",
                            "Assessed by nurse",
                            "Seen by physician",
                            "911 / EMS called",
                            "Transported to ER",
                            "Hospitalized",
                          ]}
                          value={incidentNoteData.injurySustained.medicalAttention}
                          onChange={(val) =>
                            setIncidentNoteData((prev) => ({
                              ...prev,
                              injurySustained: {
                                ...prev.injurySustained,
                                medicalAttention: val,
                              },
                            }))
                          }
                          searchable
                          rightSection={
                            <LuChevronDown size={14} color="#6B7280" />
                          }
                          rightSectionPointerEvents="none"
                          classNames={{
                            input: "mc-mantine-select-input",
                            dropdown: "mc-mantine-dropdown",
                          }}
                        />
                      </div>
                    </div>
                  </>
                )}

                <div className="mc-incident-divider" />

                <div className="mc-form-field">
                  <label className="mc-form-label">
                    Were emergency services (911 / EMS) called? *
                  </label>
                  {renderYesNoToggle(
                    incidentNoteData.emergencyServicesCalled,
                    () =>
                      setIncidentNoteData((prev) => ({
                        ...prev,
                        emergencyServicesCalled:
                          prev.emergencyServicesCalled &&
                          typeof prev.emergencyServicesCalled === "object"
                            ? prev.emergencyServicesCalled
                            : {
                                clientTransported: false,
                                timeEmsArrived: "",
                                receivingHospital: "",
                              },
                      })),
                    () =>
                      setIncidentNoteData((prev) => ({
                        ...prev,
                        emergencyServicesCalled: false,
                      })),
                  )}
                </div>

                {incidentNoteData.emergencyServicesCalled && (
                  <div className="mc-incident-subsection">
                    <div className="mc-form-row-2">
                      <div className="mc-form-field">
                        <label className="mc-form-label">
                          Was the client transported to the hospital? *
                        </label>
                        {renderYesNoToggle(
                          incidentNoteData.emergencyServicesCalled
                            .clientTransported,
                          () =>
                            setIncidentNoteData((prev) => ({
                              ...prev,
                              emergencyServicesCalled: {
                                ...prev.emergencyServicesCalled,
                                clientTransported: true,
                              },
                            })),
                          () =>
                            setIncidentNoteData((prev) => ({
                              ...prev,
                              emergencyServicesCalled: {
                                ...prev.emergencyServicesCalled,
                                clientTransported: false,
                              },
                            })),
                        )}
                      </div>
                      <div className="mc-form-field">
                        <label className="mc-form-label">
                          Time EMS Arrived
                        </label>
                        <input
                          className="mc-form-input"
                          type="time"
                          value={
                            incidentNoteData.emergencyServicesCalled
                              .timeEmsArrived || ""
                          }
                          onChange={(e) =>
                            setIncidentNoteData((prev) => ({
                              ...prev,
                              emergencyServicesCalled: {
                                ...prev.emergencyServicesCalled,
                                timeEmsArrived: e.target.value,
                              },
                            }))
                          }
                        />
                      </div>
                    </div>
                    {incidentNoteData.emergencyServicesCalled
                      .clientTransported && (
                      <div className="mc-form-field">
                        <label className="mc-form-label">
                          Receiving Hospital / Facility *
                        </label>
                        <input
                          className="mc-form-input"
                          type="text"
                          placeholder="e.g. Mercy General Hospital — Emergency Dept."
                          value={
                            incidentNoteData.emergencyServicesCalled
                              .receivingHospital || ""
                          }
                          onChange={(e) =>
                            setIncidentNoteData((prev) => ({
                              ...prev,
                              emergencyServicesCalled: {
                                ...prev.emergencyServicesCalled,
                                receivingHospital: e.target.value,
                              },
                            }))
                          }
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* ── 4c. Incident Report — Restraint / Physical Intervention ── */}
            {isIncidentReport && (
              <div className="mc-note-card mc-incident-card">
                <div className="mc-form-group-title-row">
                  <span className="mc-form-group-icon">
                    <LuPower />
                  </span>
                  <span className="mc-form-group-title">
                    RESTRAINT / PHYSICAL INTERVENTION
                  </span>
                </div>

                <div className="mc-form-field">
                  <label className="mc-form-label">
                    Was a restraint or physical intervention used? *
                  </label>
                  {renderYesNoToggle(
                    incidentNoteData.restraintUsed,
                    () =>
                      setIncidentNoteData((prev) => ({
                        ...prev,
                        restraintUsed:
                          prev.restraintUsed &&
                          typeof prev.restraintUsed === "object"
                            ? prev.restraintUsed
                            : { type: null, duration: "" },
                      })),
                    () =>
                      setIncidentNoteData((prev) => ({
                        ...prev,
                        restraintUsed: false,
                      })),
                  )}
                </div>

                {incidentNoteData.restraintUsed && (
                  <div className="mc-form-row-2">
                    <div className="mc-form-field">
                      <Select
                        label="Type of Intervention"
                        placeholder="Select"
                        data={[
                          "Verbal de-escalation only",
                          "Personal hold (2-person)",
                          "Physical escort",
                          "Seclusion",
                          "PRN medication",
                        ]}
                        value={incidentNoteData.restraintUsed.type}
                        onChange={(val) =>
                          setIncidentNoteData((prev) => ({
                            ...prev,
                            restraintUsed: { ...prev.restraintUsed, type: val },
                          }))
                        }
                        searchable
                        rightSection={
                          <LuChevronDown size={14} color="#6B7280" />
                        }
                        rightSectionPointerEvents="none"
                        classNames={{
                          input: "mc-mantine-select-input",
                          dropdown: "mc-mantine-dropdown",
                        }}
                      />
                    </div>
                    <div className="mc-form-field">
                      <label className="mc-form-label">
                        Duration (minutes)
                      </label>
                      <input
                        className="mc-form-input"
                        type="number"
                        placeholder="0"
                        value={incidentNoteData.restraintUsed.duration}
                        onChange={(e) =>
                          setIncidentNoteData((prev) => ({
                            ...prev,
                            restraintUsed: {
                              ...prev.restraintUsed,
                              duration: e.target.value,
                            },
                          }))
                        }
                      />
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ── 4d. Incident Report — Notifications made ── */}
            {isIncidentReport &&
              (() => {
                const selectedClients = (incidentNoteData.clientsInvolved || [])
                  .map((id) => allPatients?.find((p) => p?.id === id))
                  .filter(Boolean);

                const buildGroups = (patient) => {
                  const clientName = patient?.name || "";
                  const guardianName = `${
                    patient?.form2?.guardianFirstName || ""
                  } ${patient?.form2?.guardianLastName || ""}`.trim();
                  const guardians = [];
                  if (guardianName) {
                    guardians.push({
                      key: `${patient?.id}-guardian`,
                      name: guardianName,
                      sub: [clientName, "Guardian"].filter(Boolean).join(" · "),
                      phone: patient?.form2?.emergencyContactPhoneNumber || "",
                    });
                  }
                  const medicalProviders = [];
                  if (patient?.form2?.pcpName) {
                    medicalProviders.push({
                      key: `${patient?.id}-pcp`,
                      name: patient.form2.pcpName,
                      sub: [clientName, "Primary Physician"]
                        .filter(Boolean)
                        .join(" · "),
                      phone: patient?.form2?.pcpPhoneNumber || "",
                    });
                  }
                  (patient?.form2?.additionalProvider || []).forEach(
                    (prov, i) => {
                      if (prov?.providerName) {
                        medicalProviders.push({
                          key: `${patient?.id}-additional-${i}`,
                          name: prov.providerName,
                          sub: [clientName, prov?.providerType || "Provider"]
                            .filter(Boolean)
                            .join(" · "),
                          phone: prov?.providerPhone || "",
                        });
                      }
                    },
                  );
                  return { guardians, medicalProviders };
                };

                const groupDefs = [
                  {
                    key: "guardians",
                    label: "GUARDIANS & LEGAL REPRESENTATIVES",
                    icon: <LuShieldCheck />,
                  },
                  {
                    key: "medicalProviders",
                    label: "MEDICAL PROVIDERS",
                    icon: <LuStethoscope />,
                  },
                ];

                const allEntries = { guardians: [], medicalProviders: [] };
                selectedClients.forEach((patient) => {
                  const groups = buildGroups(patient);
                  allEntries.guardians.push(...groups.guardians);
                  allEntries.medicalProviders.push(...groups.medicalProviders);
                });

                const toggleContact = (key) =>
                  setIncidentNoteData((prev) => ({
                    ...prev,
                    notifiedContacts: {
                      ...prev.notifiedContacts,
                      [key]: !prev.notifiedContacts?.[key],
                    },
                  }));

                return (
                  <div className="mc-note-card mc-incident-card">
                    <div className="mc-form-group-title-row">
                      <span className="mc-form-group-icon">
                        <LuBell />
                      </span>
                      <span className="mc-form-group-title">
                        NOTIFICATIONS MADE
                      </span>
                    </div>
                    <div className="mc-form-hint">
                      <LuInfo size={12} />
                      Contacts on file for the selected client(s). Check
                      everyone who was notified of this incident.
                    </div>

                    {selectedClients.length === 0 ? (
                      <span className="mc-incident-notify-sub">
                        Select the client(s) involved above to see their
                        contacts here.
                      </span>
                    ) : (
                      groupDefs.map((group) => {
                        const entries = allEntries[group.key];
                        const notifiedCount = entries.filter(
                          (e) => incidentNoteData.notifiedContacts?.[e.key],
                        ).length;
                        return (
                          <div key={group.key}>
                            <div className="mc-form-group-title-row mc-form-group-title-row--sub">
                              <span className="mc-form-group-icon mc-form-group-icon--sm">
                                {group.icon}
                              </span>
                              <span className="mc-form-group-title mc-form-group-title--sm">
                                {group.label}
                              </span>
                              <span className="mc-form-group-count">
                                {notifiedCount} of {entries.length} notified
                              </span>
                            </div>
                            {entries.length === 0 ? (
                              <span className="mc-incident-notify-sub">
                                None on file.
                              </span>
                            ) : (
                              <div className="mc-form-checks">
                                {entries.map((entry) => (
                                  <div
                                    key={entry.key}
                                    className={`mc-check-item ${
                                      incidentNoteData.notifiedContacts?.[
                                        entry.key
                                      ]
                                        ? "mc-check-item--checked"
                                        : ""
                                    }`}
                                  >
                                    <div
                                      className="mc-check-row"
                                      onClick={() => toggleContact(entry.key)}
                                    >
                                      <Checkbox
                                        checked={
                                          !!incidentNoteData.notifiedContacts?.[
                                            entry.key
                                          ]
                                        }
                                        onChange={() =>
                                          toggleContact(entry.key)
                                        }
                                        onClick={(e) => e.stopPropagation()}
                                      />
                                      <span className="mc-incident-notify-avatar">
                                        {getInitials(entry.name)}
                                      </span>
                                      <span className="mc-check-name mc-incident-notify-name">
                                        {entry.name}
                                        <span className="mc-incident-notify-sub-inline">
                                          {entry.sub}
                                        </span>
                                      </span>
                                      {entry.phone && (
                                        <span className="mc-incident-notify-phone">
                                          {entry.phone}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })
                    )}

                    <div className="mc-form-field">
                      <label className="mc-form-label">
                        Notification Notes{" "}
                        <span className="mc-form-label-opt">(optional)</span>
                      </label>
                      <textarea
                        className="mc-form-textarea"
                        rows={4}
                        placeholder="Add any details — who was contacted, when, by what method, and their response. e.g. Guardian notified by phone at 2:40 PM; requested a call back from the nurse."
                        value={incidentNoteData.notificationNotes}
                        onChange={(e) =>
                          setIncidentNoteData((prev) => ({
                            ...prev,
                            notificationNotes: e.target.value,
                          }))
                        }
                      />
                    </div>
                  </div>
                );
              })()}

            {/* ── 4e. Incident Report — Follow-up & preventive action ── */}
            {isIncidentReport && (
              <div className="mc-note-card mc-incident-card">
                <div className="mc-form-group-title-row">
                  <span className="mc-form-group-icon">
                    <LuFileText />
                  </span>
                  <span className="mc-form-group-title">
                    FOLLOW-UP &amp; PREVENTIVE ACTION
                  </span>
                </div>
                <div className="mc-form-field">
                  <textarea
                    className="mc-form-textarea"
                    rows={4}
                    placeholder="Describe planned follow-up: care-plan review, environmental changes, additional supervision, staff training, or physician follow-up."
                    value={incidentNoteData.followUpAction}
                    onChange={(e) =>
                      setIncidentNoteData((prev) => ({
                        ...prev,
                        followUpAction: e.target.value,
                      }))
                    }
                  />
                </div>
              </div>
            )}

            {/* ── 5. ES entries section (when ES toggle is yes) ── */}
            {isEssentialNote && essentialSupportToggle === "yes" && (
              <div className="mc-section-block">
                <div className="mc-section-block-header">
                  <span className="mc-form-label">
                    Essential Support Entries
                  </span>
                  {essentialSupport.length < essentialSupportCount.length && (
                    <button
                      type="button"
                      className="mc-add-row-btn"
                      onClick={() => {
                        if (isListening) {
                          stopListening();
                          clearInterval(timerRef.current);
                          setRecordingTime(0);
                          setIsListening(false);
                          setListeningFor(null);
                        }
                        setEssentialSupport((prev) => {
                          setEssentialAccordion(String(prev.length));
                          return [...prev, mkInitialES()];
                        });
                      }}
                    >
                      + Add
                    </button>
                  )}
                </div>
                <Accordion
                  value={essentialAccordion}
                  onChange={setEssentialAccordion}
                  className="mc-entry-accordion"
                >
                  {essentialSupport.map((es, idx) => (
                    <Accordion.Item
                      key={es.id}
                      value={String(idx)}
                      className="mc-entry-accordion-item"
                    >
                      <Accordion.Control className="mc-entry-accordion-control">
                        <div className="mc-entry-accordion-header">
                          <span className="mc-note-card-title">
                            Entry {idx + 1}
                          </span>
                          {essentialSupport.length > 1 && (
                            <button
                              type="button"
                              className="mc-remove-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                setEssentialSupport((prev) =>
                                  prev.filter((en) => en.id !== es.id),
                                );
                              }}
                            >
                              ×
                            </button>
                          )}
                        </div>
                      </Accordion.Control>
                      <Accordion.Panel className="mc-entry-accordion-panel">
                        <div className="mc-form-field">
                          <Select
                            label="Support Type *"
                            placeholder="Select type…"
                            data={essentialSupportTypeState}
                            value={es.essentialSupportType || null}
                            onChange={(val) =>
                              handleEsTypeChange(idx, val || "")
                            }
                            searchable
                            rightSection={
                              <LuChevronDown size={14} color="#6B7280" />
                            }
                            rightSectionPointerEvents="none"
                            classNames={{
                              input: "mc-mantine-select-input",
                              dropdown: "mc-mantine-dropdown",
                            }}
                          />
                        </div>
                        <div className="mc-form-field">
                          <Select
                            label="How Often"
                            placeholder="Select frequency…"
                            data={frequencyOptions[idx] || []}
                            value={es.howOftenEssentialSupport || null}
                            onChange={(val) =>
                              handleEsFrequencyChange(idx, val || "")
                            }
                            searchable
                            rightSection={
                              <LuChevronDown size={14} color="#6B7280" />
                            }
                            rightSectionPointerEvents="none"
                            classNames={{
                              input: "mc-mantine-select-input",
                              dropdown: "mc-mantine-dropdown",
                            }}
                          />
                        </div>
                        {/* Outcome # — mirrors desktop essentialNumber dropdown */}
                        {(essentialNumbers[idx] || []).length > 0 && (
                          <div className="mc-form-field">
                            <Select
                              label="Outcome #"
                              placeholder="Select #…"
                              data={essentialNumbers[idx] || []}
                              value={es.essentialNumber || null}
                              onChange={(val) =>
                                setEssentialSupport((prev) =>
                                  prev.map((item, i) =>
                                    i === idx
                                      ? {
                                          ...item,
                                          essentialNumber: val || "",
                                          essentialActivityNumber: "",
                                        }
                                      : item,
                                  ),
                                )
                              }
                              searchable
                              rightSection={
                                <LuChevronDown size={14} color="#6B7280" />
                              }
                              rightSectionPointerEvents="none"
                              classNames={{
                                input: "mc-mantine-select-input",
                                dropdown: "mc-mantine-dropdown",
                              }}
                            />
                          </div>
                        )}
                        <div className="mc-form-field">
                          <Select
                            label={
                              essentialSupportErrors[idx] &&
                              !es.essentialActivityNumber
                                ? "Activity * — Support Activity is required"
                                : "Activity *"
                            }
                            placeholder="Select activity…"
                            data={(activities[idx] || [])
                              .filter(
                                (a) =>
                                  !essentialSupport.some(
                                    (s, si) =>
                                      // eslint-disable-next-line eqeqeq
                                      si !== idx &&
                                      s.essentialActivityNumber == a.id,
                                  ),
                              )
                              .map((a) => ({
                                value: String(a.id),
                                label: a.supportStatement,
                              }))}
                            value={
                              es.essentialActivityNumber
                                ? String(es.essentialActivityNumber)
                                : null
                            }
                            onChange={(val) =>
                              handleEsActivityChange(idx, val || "")
                            }
                            error={
                              essentialSupportErrors[idx] &&
                              !es.essentialActivityNumber
                                ? " "
                                : undefined
                            }
                            nothingFound="Activities already added"
                            classNames={{
                              input: `mc-mantine-select-input${essentialSupportErrors[idx] && !es.essentialActivityNumber ? " mc-input--error" : ""}`,
                              label: "mc-form-label",
                              dropdown: "mc-mantine-dropdown",
                            }}
                          />
                        </div>
                        {/* EssentialViewInfo — mirrors desktop EssentialViewInfo component */}
                        {(() => {
                          const esData = essentialSupport[idx];
                          const actList = Array.isArray(esData?.activities)
                            ? esData.activities.filter(
                                // eslint-disable-next-line eqeqeq
                                (a) => a.id == esData.essentialActivityNumber,
                              )
                            : [];
                          const selAct = actList[0];
                          const howOften =
                            selAct?.howOftenEssentialSupport ||
                            esData?.howOftenEssentialSupport ||
                            "";
                          if (!actList.length && !howOften) return null;
                          return (
                            <details className="mc-viewinfo">
                              <summary>
                                ℹ Info: Essential Support Activities
                              </summary>
                              <div className="mc-viewinfo-body">
                                {actList.map((a, ai) => (
                                  <div
                                    key={a.id || ai}
                                    className="mc-viewinfo-activity"
                                  >
                                    <span className="mc-viewinfo-activity-num">
                                      {ai + 1}
                                    </span>
                                    <div className="mc-viewinfo-cols">
                                      {/* Support Statement column */}
                                      <div className="mc-viewinfo-col">
                                        <span className="mc-viewinfo-label">
                                          Support Statement
                                        </span>
                                        <span className="mc-viewinfo-value">
                                          {a?.supportStatement || "-"}
                                        </span>
                                      </div>
                                      {/* How to Support column — each item its own box */}
                                      <div className="mc-viewinfo-col">
                                        <span className="mc-viewinfo-label">
                                          How to Support
                                        </span>
                                        {Array.isArray(a?.howToSupport) &&
                                        a.howToSupport.length > 0 ? (
                                          a.howToSupport.map((h, hi) => (
                                            <span
                                              key={h?.id || hi}
                                              className="mc-viewinfo-value"
                                            >
                                              {h?.text || h || "-"}
                                            </span>
                                          ))
                                        ) : (
                                          <span className="mc-viewinfo-value">
                                            {typeof a?.howToSupport === "string"
                                              ? a.howToSupport || "-"
                                              : "-"}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                ))}
                                {howOften && (
                                  <div className="mc-viewinfo-row">
                                    <span className="mc-viewinfo-label">
                                      How Often
                                    </span>
                                    <span className="mc-viewinfo-value">
                                      {howOften}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </details>
                          );
                        })()}
                        {renderTextareaField({
                          label: "Description",
                          value: es.description || "",
                          onChange: (val) =>
                            setEssentialSupport((prev) =>
                              prev.map((item, i) =>
                                i === idx
                                  ? { ...item, description: val }
                                  : item,
                              ),
                            ),
                          fieldKey: `es-${idx}`,
                          rows: 4,
                        })}
                      </Accordion.Panel>
                    </Accordion.Item>
                  ))}
                </Accordion>
              </div>
            )}

            {/* ── 6. Intervention goals section (when Intervention toggle is yes) ── */}
            {isInterventionNote && interventionPlanToggle === "yes" && (
              <div className="mc-section-block">
                <div className="mc-section-block-header">
                  <span className="mc-form-label">Goals</span>
                  {goals.length < goalOptions.length && (
                    <button
                      type="button"
                      className="mc-add-row-btn"
                      onClick={() => {
                        if (isListening) {
                          stopListening();
                          clearInterval(timerRef.current);
                          setRecordingTime(0);
                          setIsListening(false);
                          setListeningFor(null);
                        }
                        setGoals((prev) => {
                          setGoalsAccordion(String(prev.length));
                          return [...prev, mkInitialGoal()];
                        });
                        setGoalItems((prev) => [
                          ...prev,
                          [
                            {
                              id: Date.now(),
                              objectives: "",
                              Intervention_Instruction: "",
                            },
                          ],
                        ]);
                      }}
                    >
                      + Add Goal
                    </button>
                  )}
                </div>
                {goals.map((goal, idx) => (
                  <div key={goal.id} className="mc-note-card">
                    <div className="mc-note-card-head">
                      <span className="mc-note-card-title">Goal {idx + 1}</span>
                      {goals.length > 1 && (
                        <button
                          type="button"
                          className="mc-remove-btn"
                          onClick={() => {
                            setGoals((prev) =>
                              prev.filter((g) => g.id !== goal.id),
                            );
                            setGoalItems((prev) =>
                              prev.filter((_, i) => i !== idx),
                            );
                          }}
                        >
                          ×
                        </button>
                      )}
                    </div>

                    <div className="mc-form-field">
                      <Select
                        label="Identified Goal"
                        placeholder="Select goal…"
                        data={goalOptions}
                        value={goal.identifiedGoal || null}
                        onChange={(val) => handleGoalChange(idx, val || "")}
                        searchable
                        rightSection={
                          <LuChevronDown size={14} color="#6B7280" />
                        }
                        rightSectionPointerEvents="none"
                        classNames={{
                          input: "mc-mantine-select-input",
                          dropdown: "mc-mantine-dropdown",
                        }}
                      />
                    </div>

                    {goal.goalNumber && (
                      <div className="mc-form-field">
                        <label className="mc-form-label">Goal Number</label>
                        <input
                          className="mc-form-input"
                          type="text"
                          value={goal.goalNumber}
                          readOnly
                        />
                      </div>
                    )}

                    <div className="mc-form-row-2">
                      <div className="mc-form-field">
                        <label className="mc-form-label">Time In</label>
                        <input
                          className="mc-form-input"
                          type="time"
                          value={goal.startTimeGoal || ""}
                          onChange={(e) =>
                            setGoals((prev) =>
                              prev.map((g, i) =>
                                i === idx
                                  ? { ...g, startTimeGoal: e.target.value }
                                  : g,
                              ),
                            )
                          }
                        />
                      </div>
                      <div className="mc-form-field">
                        <label className="mc-form-label">Time Out</label>
                        <input
                          className="mc-form-input"
                          type="time"
                          value={goal.endTimeGoal || ""}
                          onChange={(e) =>
                            setGoals((prev) =>
                              prev.map((g, i) =>
                                i === idx
                                  ? { ...g, endTimeGoal: e.target.value }
                                  : g,
                              ),
                            )
                          }
                        />
                      </div>
                    </div>

                    {/* Goal activities */}
                    <div className="mc-form-field">
                      <label className="mc-form-label">Activities</label>
                      {(goalItems[idx] || []).map((item, ai) => (
                        <div key={item.id} className="mc-goal-activity-row">
                          <div className="mc-form-row-2" style={{ flex: 1 }}>
                            <Select
                              placeholder="Objective…"
                              data={objectivesByGoal.filter(
                                (o) =>
                                  !(goalItems[idx] || []).some(
                                    (g) =>
                                      g.objectives === o.value &&
                                      g.id !== item.id,
                                  ),
                              )}
                              value={item.objectives || null}
                              onChange={(val) =>
                                handleGoalActivityChange(
                                  item.id,
                                  "objectives",
                                  val || "",
                                  ai,
                                  idx,
                                )
                              }
                              searchable
                              rightSection={
                                <LuChevronDown size={14} color="#6B7280" />
                              }
                              rightSectionPointerEvents="none"
                              classNames={{
                                input: "mc-mantine-select-input",
                                dropdown: "mc-mantine-dropdown",
                              }}
                            />
                            <Select
                              placeholder="Instruction…"
                              data={interventionsByGoal.filter(
                                (o) =>
                                  !(goalItems[idx] || []).some(
                                    (g) =>
                                      g.Intervention_Instruction === o.value &&
                                      g.id !== item.id,
                                  ),
                              )}
                              value={item.Intervention_Instruction || null}
                              onChange={(val) =>
                                handleGoalActivityChange(
                                  item.id,
                                  "Intervention_Instruction",
                                  val || "",
                                  ai,
                                  idx,
                                )
                              }
                              searchable
                              rightSection={
                                <LuChevronDown size={14} color="#6B7280" />
                              }
                              rightSectionPointerEvents="none"
                              classNames={{
                                input: "mc-mantine-select-input",
                                dropdown: "mc-mantine-dropdown",
                              }}
                            />
                          </div>
                          {(goalItems[idx] || []).length > 1 && (
                            <button
                              type="button"
                              className="mc-remove-btn"
                              style={{ marginLeft: 6 }}
                              onClick={() => {
                                const ni = [...goalItems];
                                ni[idx] = ni[idx].filter(
                                  (it) => it.id !== item.id,
                                );
                                setGoalItems(ni);
                                setGoals((prev) =>
                                  prev.map((g, i) =>
                                    i === idx
                                      ? { ...g, goalActivities: ni[idx] }
                                      : g,
                                  ),
                                );
                              }}
                            >
                              ×
                            </button>
                          )}
                        </div>
                      ))}
                      {(goalItems[idx] || []).length <
                        Math.max(
                          objectivesByGoal.length,
                          interventionsByGoal.length,
                          1,
                        ) && (
                        <button
                          type="button"
                          className="mc-add-row-btn"
                          style={{ marginTop: 6 }}
                          onClick={() => {
                            const ni = [...goalItems];
                            ni[idx] = [
                              ...(ni[idx] || []),
                              {
                                id: Date.now(),
                                objectives: "",
                                Intervention_Instruction: "",
                              },
                            ];
                            setGoalItems(ni);
                          }}
                        >
                          + Add Activity
                        </button>
                      )}
                    </div>

                    {renderTextareaField({
                      label: "Behavior Description",
                      value: goal.behaviorDescription || "",
                      onChange: (val) =>
                        setGoals((prev) =>
                          prev.map((g, i) =>
                            i === idx ? { ...g, behaviorDescription: val } : g,
                          ),
                        ),
                      fieldKey: `goal-behavior-${idx}`,
                      rows: 3,
                      goalPromptType: "behavior",
                    })}
                    {renderTextareaField({
                      label: "Response Description",
                      value: goal.responseDescription || "",
                      onChange: (val) =>
                        setGoals((prev) =>
                          prev.map((g, i) =>
                            i === idx ? { ...g, responseDescription: val } : g,
                          ),
                        ),
                      fieldKey: `goal-response-${idx}`,
                      rows: 3,
                      goalPromptType: "response",
                    })}
                    {renderTextareaField({
                      label: "Plan Description",
                      value: goal.planDescription || "",
                      onChange: (val) =>
                        setGoals((prev) =>
                          prev.map((g, i) =>
                            i === idx ? { ...g, planDescription: val } : g,
                          ),
                        ),
                      fieldKey: `goal-plan-${idx}`,
                      rows: 3,
                      goalPromptType: "plan",
                    })}

                    {/* Was this goal met? — after Plan Description, mirrors desktop */}
                    <div className="mc-form-field">
                      <Select
                        label="Was this goal met?"
                        placeholder="Select a response"
                        data={[
                          { value: "Yes", label: "Yes" },
                          { value: "No", label: "No" },
                          {
                            value: "Goal in progress",
                            label: "Goal in progress",
                          },
                          { value: "Not Applicable", label: "Not Applicable" },
                        ]}
                        value={goal.metGoalIntervention || null}
                        onChange={(val) =>
                          setGoals((prev) =>
                            prev.map((g, i) =>
                              i === idx
                                ? { ...g, metGoalIntervention: val || "" }
                                : g,
                            ),
                          )
                        }
                        searchable
                        rightSection={
                          <LuChevronDown size={14} color="#6B7280" />
                        }
                        rightSectionPointerEvents="none"
                        classNames={{
                          input: "mc-mantine-select-input",
                          dropdown: "mc-mantine-dropdown",
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* ── 7. Support Plan outcomes section (when Support Plan toggle is yes) ── */}
            {supportPlan === "yes" && (
              <div className="mc-section-block">
                <div className="mc-section-block-header">
                  <span className="mc-form-label">Outcomes</span>
                  {descriptions.length < outComeTitle.length && (
                    <button
                      type="button"
                      className="mc-add-row-btn"
                      onClick={() => {
                        if (isListening) {
                          stopListening();
                          clearInterval(timerRef.current);
                          setRecordingTime(0);
                          setIsListening(false);
                          setListeningFor(null);
                        }
                        setDescriptions((prev) => {
                          setOpenedAccordion(String(prev.length));
                          return [...prev, mkInitialDesc()];
                        });
                      }}
                    >
                      + Add Outcome
                    </button>
                  )}
                </div>
                {descriptions.map((desc, idx) => (
                  <div key={desc.id} className="mc-note-card">
                    <div className="mc-note-card-head">
                      <span className="mc-note-card-title">
                        Outcome {desc.details?.outcomeNumber || idx + 1}
                      </span>
                      {descriptions.length > 1 && (
                        <button
                          type="button"
                          className="mc-remove-btn"
                          onClick={() =>
                            setDescriptions((prev) =>
                              prev.filter((d) => d.id !== desc.id),
                            )
                          }
                        >
                          ×
                        </button>
                      )}
                    </div>

                    <div className="mc-form-field">
                      <Select
                        label={
                          errors[idx] ? "Outcome Title *" : "Outcome Title"
                        }
                        placeholder="Select outcome…"
                        data={outComeTitle}
                        value={desc.outcomeTitle || null}
                        onChange={(val) =>
                          handleOutcomeChange(idx, "outcomeTitle", val || "")
                        }
                        error={desc.error || errors[idx] || undefined}
                        classNames={{
                          input: `mc-mantine-select-input${errors[idx] ? " mc-input--error" : ""}`,
                          label: "mc-form-label",
                          dropdown: "mc-mantine-dropdown",
                        }}
                      />
                    </div>

                    <div className="mc-form-field">
                      <Select
                        label="Outcome Number"
                        placeholder="Select outcome #…"
                        data={outcomeNumbers}
                        value={desc.outcomeNumber || null}
                        onChange={(val) =>
                          handleOutcomeChange(idx, "outcomeNumber", val || "")
                        }
                        searchable
                        rightSection={
                          <LuChevronDown size={14} color="#6B7280" />
                        }
                        rightSectionPointerEvents="none"
                        classNames={{
                          input: "mc-mantine-select-input",
                          dropdown: "mc-mantine-dropdown",
                        }}
                      />
                    </div>

                    {desc.outComeCategory && (
                      <div className="mc-form-field">
                        <label className="mc-form-label">
                          Outcome Category
                        </label>
                        <input
                          className="mc-form-input"
                          type="text"
                          value={desc.outComeCategory || ""}
                          readOnly
                        />
                      </div>
                    )}

                    {/* ViewInfo — mirrors desktop ViewInfo component */}
                    {desc.details && (
                      <details className="mc-viewinfo">
                        <summary>
                          ℹ Info:{" "}
                          {desc.details?.outcomeTitle || "Outcome Details"}
                        </summary>
                        <div className="mc-viewinfo-body">
                          {(desc.details?.activities || []).map((a, ai) => (
                            <div key={ai} className="mc-viewinfo-activity">
                              <span className="mc-viewinfo-activity-num">
                                {a?.activityNumber || ai + 1}
                              </span>
                              <div className="mc-viewinfo-cols">
                                {/* Activities column */}
                                <div className="mc-viewinfo-col">
                                  <span className="mc-viewinfo-label">
                                    Activity Statement
                                  </span>
                                  <span className="mc-viewinfo-value">
                                    {a?.supportActivity || "-"}
                                  </span>
                                </div>
                                {/* Instructions column — each instruction its own box */}
                                <div className="mc-viewinfo-col">
                                  <span className="mc-viewinfo-label">
                                    How to Support
                                  </span>
                                  {typeof a?.supportInstructions ===
                                  "string" ? (
                                    <span className="mc-viewinfo-value">
                                      {a.supportInstructions || "-"}
                                    </span>
                                  ) : Array.isArray(a?.supportInstructions) &&
                                    a.supportInstructions.length > 0 ? (
                                    a.supportInstructions.map((s, si) => (
                                      <span
                                        key={s?.id || si}
                                        className="mc-viewinfo-value"
                                      >
                                        {s?.text || s || "-"}
                                      </span>
                                    ))
                                  ) : (
                                    <span className="mc-viewinfo-value">-</span>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                          {desc.details?.howOften && (
                            <div className="mc-viewinfo-row">
                              <span className="mc-viewinfo-label">
                                How Often
                              </span>
                              <span className="mc-viewinfo-value">
                                {desc.details.howOften}
                              </span>
                            </div>
                          )}
                          {desc.details?.additionalInformation && (
                            <div className="mc-viewinfo-row">
                              <span className="mc-viewinfo-label">
                                Additional Note
                              </span>
                              <span className="mc-viewinfo-value">
                                {desc.details.additionalInformation}
                              </span>
                            </div>
                          )}
                        </div>
                      </details>
                    )}
                    {renderTextareaField({
                      label: "Description",
                      value: desc.description || "",
                      onChange: (val) =>
                        setDescriptions((prev) =>
                          prev.map((d, i) =>
                            i === idx ? { ...d, description: val } : d,
                          ),
                        ),
                      fieldKey: `desc-${idx}`,
                      rows: 4,
                    })}

                    <div className="mc-form-field">
                      <Select
                        label="Was this goal met?"
                        placeholder="Select a response"
                        data={[
                          { value: "Yes", label: "Yes" },
                          { value: "No", label: "No" },
                          {
                            value: "Goal in progress",
                            label: "Goal in progress",
                          },
                          { value: "Not Applicable", label: "Not Applicable" },
                        ]}
                        value={desc.metGoal || null}
                        onChange={(val) =>
                          setDescriptions((prev) =>
                            prev.map((d, i) =>
                              i === idx ? { ...d, metGoal: val || "" } : d,
                            ),
                          )
                        }
                        searchable
                        rightSection={
                          <LuChevronDown size={14} color="#6B7280" />
                        }
                        rightSectionPointerEvents="none"
                        classNames={{
                          input: "mc-mantine-select-input",
                          dropdown: "mc-mantine-dropdown",
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* ── 8. Signature — mirrors desktop: show when no toggle section is open, or when supportPlan is yes ── */}
            {supportPlan !== "yes" &&
              essentialSupportToggle !== "yes" &&
              interventionPlanToggle !== "yes" &&
              renderSignature()}
            {supportPlan === "yes" && renderSignature()}
          </>
        )}
      </div>

      {/* Footer */}
      <div className="mc-form-footer">
        <button
          className="mc-form-cancel-btn"
          type="button"
          onClick={() => onTabSwitch("facesheet")}
          disabled={formLoading}
        >
          Cancel
        </button>
        <button
          className="mc-form-save-btn"
          type="button"
          onClick={handleSave}
          disabled={!noteTypeObj || formLoading}
        >
          {formLoading ? "Saving…" : "Save note"}
        </button>
      </div>

      {/* ── Pending Approval / Success Modal (mirrors desktop NoteDrawer) ── */}
      {noteTypeObj && (
        <CustomModal
          modalState={pendingModal}
          modalTopImg={pendingModalIcon}
          modalTitle={
            isIncidentReport
              ? "Pending Supervisor Approval"
              : noteTypeApproval
              ? "Pending Approval"
              : "Note Successfully Saved"
          }
          modalTextOne={
            isIncidentReport
              ? "The Incident Report has been submitted for Supervisor approval."
              : noteTypeApproval
              ? "The note has been submitted for admin approval."
              : null
          }
          modalTextTwo={
            isIncidentReport
              ? "A Supervisor will review and sign off on it — you'll be"
              : noteTypeApproval
              ? "Please check back to see if it was approved or"
              : null
          }
          modalTextThree={
            isIncidentReport
              ? "notified of the outcome."
              : noteTypeApproval
              ? "denied."
              : null
          }
          closeModal={(val) => {
            setPendingModal(val);
            onTabSwitch("facesheet");
          }}
          colorOne="yellow"
          variantOne="filled"
          btnTextOne="OK"
          noteType={noteTypeObj}
          isStaffWithoutApproval={!noteTypeApproval}
        />
      )}
    </div>
  );
};

export default MobileChart;
